import { Application, Container, Graphics, RenderLayer, type WebGPURenderer } from "pixi.js";
import { type StoreApi } from "zustand/vanilla";
import { type ArrangeOp, type DocState } from "../model/store.ts";
import {
  type Card,
  type Edge,
  DEFAULT_FROM_END,
  DEFAULT_TO_END,
  DEFAULT_ROUTE,
  DEFAULT_EDGE_COLOR,
  type ShapeKind,
  type TextAlign,
  createId,
  DEFAULT_SHAPE_FILL,
} from "../model/types.ts";
import { type Highlight } from "../model/highlights.ts";
import { Camera } from "./Camera.ts";
import {
  CardView,
  resizeDirAt as resizeDirOn,
  type HitBand,
  type ResizeDir,
  type SelectionChrome,
} from "./CardView.ts";
import { GroupView } from "./GroupView.ts";
import { GroupLabels, GROUP_LABEL_GAP_PX, type GroupLabelItem } from "./GroupLabels.ts";
import { ShapeView, paintShapePath } from "./ShapeView.ts";
import {
  CanvasTextEditor,
  CanvasTextLabels,
  type CanvasTextItem,
  type TextCover,
} from "./canvasText.ts";
import { TextureCache } from "./textureCache.ts";
import { isImagePath, isPdfPath } from "../vault/attachments.ts";
import { CardActions } from "./CardActions.ts";
import { pdfCoverSize } from "./pdfDocument.ts";
import { inkColor, paintStroke } from "./ink.ts";
import { UI_PAPER, UI_SELECTION } from "./uiColors.ts";
import {
  fitHeight,
  hitsShape,
  shapeCover,
  textBox,
  textColorFor,
  SHAPE_DEFAULT_H,
  SHAPE_DEFAULT_W,
  SHAPE_MIN_H,
  SHAPE_MIN_W,
} from "./shapeStyle.ts";
import {
  hasTextBackground,
  textNodeBox,
  textNodeSize,
  TEXT_MIN_H,
  TEXT_MIN_W,
  TEXT_RADIUS,
} from "./textStyle.ts";
import { EdgeLayer } from "./EdgeLayer.ts";
import { openContextMenu, type ContextMenuItem } from "../app/dialogs.ts";
import { autoSides } from "./edgeGeometry.ts";
import { boundsOf, snapMove, snapResize, type SnapRect, type SnapResult } from "./snapping.ts";
import { CanvasToolbar, toolCursor, type Tool } from "./CanvasToolbar.ts";
import { InlineInput } from "./InlineInput.ts";
import { EdgeLabels, type EdgeLabelItem } from "./EdgeLabels.ts";
import { EdgeActions } from "./EdgeActions.ts";
import { endpointAt } from "./EdgePorts.ts";
import { ConnectGesture } from "./ConnectGesture.ts";
import { CanvasClipboard } from "./CanvasClipboard.ts";
import { CommentLayer, type CommentItem } from "./CommentLayer.ts";
import { DevHud, hudEnabled } from "./devHud.ts";
import { buildStressDiagram } from "./devStress.ts";

type Mode =
  | "none"
  | "pan"
  | "move"
  | "resize"
  | "marquee"
  | "draw"
  | "draw-container"
  | "draw-shape"
  | "connect";

/**
 * Lo que hay bajo el puntero. El hit-test dejó de devolver `Card` cuando
 * aparecieron los conectores: una flecha es seleccionable pero no es una
 * tarjeta, y hacer que "id seleccionado" pudiera ser de cualquiera de los dos
 * obligaría a preguntar de qué tipo es en cada uso.
 */
type Hit = { kind: "card"; card: Card } | { kind: "edge"; edge: Edge };

export interface CanvasViewOptions {
  /**
   * ¿Le toca a este lienzo atender el teclado?
   *
   * Sus atajos escuchan en `window` —tienen que llegar esté el foco donde esté
   * adentro del lienzo— y eso los hace llegar también desde DOM ajeno. Con el
   * panel lateral abierto y el foco puesto ahí, `Supr` borraba la selección del
   * lienzo que había quedado a la izquierda. Sin esta opción el lienzo se queda
   * con el teclado, que es lo correcto cuando es lo único que hay.
   */
  hasKeyboard?: () => boolean;
  /** Read a vault document's contents, to fill in a `file` node's body. */
  loadFile?: (path: string) => Promise<string>;
  /**
   * Open a vault document in the workspace.
   *
   * `peek` pide el panel lateral en vez del área grande. Es lo que manda el
   * doble click sobre una tarjeta: consultar la nota no debería costar el
   * lienzo entero. El botón "Full view" de la tarjeta pide lo otro.
   */
  onOpenFile?: (path: string, opts: { peek: boolean }) => void;
  /** Persist a pasted image into the vault; returns the vault-relative path. */
  saveImage?: (blob: Blob, suggestedName: string) => Promise<string>;
  /** Read an attachment's bytes from the vault (for rendering image nodes). */
  loadBinary?: (path: string) => Promise<Blob>;
  /** Read a document's highlights, so a `file` node shows them like the document view does. */
  loadHighlights?: (path: string) => Promise<Highlight[]>;
}

// Wheel/trackpad zoom sensitivity (per normalized px of scroll). Higher = faster.
const ZOOM_SPEED_WHEEL = 0.0075;
const ZOOM_SPEED_PINCH = 0.015;

// Pan (translation) sensitivity multiplier. 1 = 1:1 with the pointer/trackpad;
// 1.5 makes panning 50% faster.
const PAN_SPEED = 1.5;

// Pencil (draw tool) ink. Width is in world px so strokes zoom with the
// canvas, FigJam-style. Active color is settable via the color palette.
const INK_WIDTH = 3;

// Min pointer travel (screen px) before a new point is appended to a stroke —
// filters out jitter and keeps point counts down.
const INK_MIN_DIST = 1.5;

/** MIME-like key used for sidebar → canvas drag & drop of a vault file. */
export const DND_PATH_TYPE = "text/notespace-path";

// Tabler "focus" icon (MIT), re-stroked to 1.7 to match the toolbar's line weight.
const FOCUS_SVG =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M3 7v-4h4"/><path d="M17 3h4v4"/><path d="M21 17v4h-4"/><path d="M7 21h-4v-4"/></svg>';

/** Presets del menú de zoom (50%, 70%, 90%). */
const ZOOM_PRESETS = [0.5, 0.7, 0.9];
const PREF_ZOOM_KEY = "notespace:preferred-zoom";
const CUSTOM_ZOOMS_KEY = "notespace:custom-zooms";

function loadPreferredZoom(): number | null {
  try {
    const raw = localStorage.getItem(PREF_ZOOM_KEY);
    if (!raw) return null;
    const v = Number(raw);
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

function loadCustomZooms(): number[] {
  try {
    const raw = localStorage.getItem(CUSTOM_ZOOMS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr
      .map(Number)
      .filter((v) => Number.isFinite(v) && v > 0)
      .slice(0, 20);
  } catch {
    return [];
  }
}

/**
 * ¿El foco está en algo donde se escribe? El canvas escucha teclas en `window`,
 * así que sin esto sus atajos le roban las teclas a cualquier campo: `Space` se
 * come el espacio (lo reserva para el pan), `Backspace` borra las tarjetas
 * seleccionadas y las letras sueltas cambian de herramienta.
 *
 * Va por capacidad de edición, no por una lista de clases: cualquier input,
 * textarea o `contenteditable` cuenta, y eso incluye a CodeMirror esté donde
 * esté (su `.cm-content` es contenteditable) — el editor de documentos, el de
 * tarjetas y los diálogos entran todos por la misma puerta.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el?.closest) return false;
  return !!el.closest(
    "input, textarea, select, [contenteditable]:not([contenteditable='false'])",
  );
}

/**
 * La esquina opuesta que deja el arrastre cuadrado: el lado más largo manda y el
 * otro lo copia, conservando hacia qué lado se está arrastrando.
 */
function squareCorner(
  startX: number,
  startY: number,
  x: number,
  y: number,
): { x: number; y: number } {
  const dx = x - startX;
  const dy = y - startY;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return {
    x: startX + Math.sign(dx || 1) * side,
    y: startY + Math.sign(dy || 1) * side,
  };
}

/** Radio de click de una arista, en px de PANTALLA (se divide por el zoom). */
const EDGE_HIT_PX = 8;

/** Debajo de este zoom las etiquetas de los conectores se ocultan. */
const LABEL_MIN_ZOOM = 0.25;

/** Desplazamiento de una copia respecto del original, en px de mundo. */
const DUPLICATE_OFFSET = 40;

/** Fuerza del imán de alineación, en px de PANTALLA (se divide por el zoom). */
const SNAP_PX = 6;

/** Medidas de la barrita que anuncia un hueco repartido, en px de PANTALLA. */
const GAP_CAP_PX = 4; // medio alto de los topes de las puntas
const GAP_MIN_PX = 3; // hueco más chico que se puede dibujar sin que sea un borrón

/**
 * El contenedor se agarra con una banda más ancha que una tarjeta y entra más
 * hacia adentro: es grande, casi siempre está vacío en el borde, y ahí no hay
 * nada que se pueda querer arrastrar en su lugar.
 */
const GROUP_HIT_BAND: HitBand = { outset: 8, inset: 9, corner: 20 };

/** Cursor del sistema para cada dirección de resize. */
function resizeCursor(dir: ResizeDir | null): string {
  switch (dir) {
    case "nw":
    case "se":
      return "nwse-resize";
    case "ne":
    case "sw":
      return "nesw-resize";
    case "n":
    case "s":
      return "ns-resize";
    case "e":
    case "w":
      return "ew-resize";
    default:
      return "default";
  }
}

/** Largo del trazo y del hueco del punteado de las guías, en px de pantalla. */

/**
 * Orquestador del render. Dueño de su DOM dentro del host, de la app de Pixi,
 * del contenedor de mundo y la cámara, del culling y el LOD por frame, de la
 * máquina de gestos del puntero y del puente con el editor del DOM. Un
 * CanvasView dibuja exactamente un documento `.canvas`; el Workspace lo destruye
 * y arma otro al cambiar de documento.
 *
 * Lo que NO vive acá, y por qué:
 *
 * | Módulo             | Se llevó                                              |
 * |--------------------|-------------------------------------------------------|
 * | `CanvasToolbar`    | La barra y la paleta: DOM que sólo habla por callbacks |
 * | `GroupRename`      | El input flotante sobre la etiqueta de un contenedor   |
 * | `ConnectGesture`   | Puertos, arrastre y alta de un conector                |
 * | `CanvasClipboard`  | Copiar, pegar y soltar archivos                        |
 *
 * El criterio del corte fue la frontera, no el largo: se fue lo que se comunica
 * por unos pocos callbacks. La máquina de gestos (pointerdown/move/up con su
 * estado) se queda porque depende de casi todo lo demás —cámara, store,
 * selección, editor, caché, vistas—, y sacarla obligaría a pasarle un objeto
 * con media clase adentro: movería líneas sin separar nada.
 */
/** El menú Arrange de Figma. */
const ARRANGE_MENU: ContextMenuItem[] = [
  { id: "front", label: "Bring to front" },
  { id: "forward", label: "Bring forward" },
  { id: "backward", label: "Send backward" },
  { id: "back", label: "Send to back" },
];

/** Cuánto se espera sin actividad antes de dejar de dibujar (ver `lastWake`). */
const IDLE_MS = 1000;

/** Los eventos del usuario que sacan al lienzo del reposo. */
const WAKE_EVENTS = [
  "pointerdown",
  "pointermove",
  "pointerup",
  "wheel",
  "keydown",
  "keyup",
  "input",
] as const;

export class CanvasView {
  private app = new Application();
  private world = new Container();
  private marquee!: Graphics;
  /** Guías de alineación mientras se mueve algo. Pantalla, como la marquesina. */
  private guides!: Graphics;
  // Live preview of the stroke being drawn (committed strokes are `ink` cards
  // rendered by their CardView). Lives inside `world`, so it pans/zooms with
  // the camera; kept above cards (see syncViews).
  private inkLayer = new Graphics();
  /**
   * Capa propia para los recuadros de contenedor. Antes se insertaban en el
   * mundo con un `addChildAt(…, 1)` a mano, que dejaba el orden dependiendo de
   * un índice mágico; con un contenedor aparte el apilado queda declarado en un
   * solo lugar (ver `init`): contenedores → conectores → tarjetas → tinta.
   */
  private groupsLayer = new Container();
  /** Los conectores. Debajo de las tarjetas para no taparles el contenido. */
  private edgeLayer = new EdgeLayer();
  /**
   * El cromo de selección de las tarjetas, dibujado arriba de TODAS.
   *
   * Las tarjetas se apilan en el orden del documento y elegir una no la sube
   * (como en Figma): si su recuadro viajara con ella, una tarjeta de encima lo
   * taparía y no se vería qué está elegido. Con un `RenderLayer` el recuadro
   * sigue siendo hijo de su tarjeta —se mueve con ella— pero se dibuja acá.
   */
  private chromeLayer = new RenderLayer();
  /** Puertos, arrastre y alta de conectores. */
  private connect!: ConnectGesture;
  private camera!: Camera;
  private cache = new TextureCache();
  private views = new Map<string, CardView>();
  private groupViews = new Map<string, GroupView>();
  private dpr = Math.max(1, window.devicePixelRatio || 1);

  // Owned DOM.
  private root!: HTMLElement;
  private canvas!: HTMLCanvasElement;
  private toolbar!: CanvasToolbar;
  private cardActions!: CardActions;
  private groupLabels = new GroupLabels();
  private shapeViews = new Map<string, ShapeView>();
  /** Todo el texto del lienzo —el de las formas y el de los nodos sueltos— en
   *  UNA capa: son la misma clase de cosa (ver `canvasText.ts`). */
  private textLabels = new CanvasTextLabels();
  /** El editor de ese texto. Se monta en la capa de las etiquetas: es el mismo
   *  contenedor posicionado, así ocupa el lugar exacto del texto que reemplaza. */
  private textEditor = new CanvasTextEditor(this.textLabels.root);
  /** Comentarios anclados: círculo + burbuja flotante. */
  private commentLayer = new CommentLayer();
  /** Sólo con `?hud` o `localStorage.blanqo:hud`. Ver `devHud.ts`. */
  private hud: DevHud | null = null;
  /** Tarjetas dibujadas en el último frame, para el HUD. */
  private visibleCards = 0;
  /** Controles de navegación: porcentaje de zoom en vivo y autoenfoque. */
  private navControls!: HTMLElement;
  private navZoom!: HTMLButtonElement;
  private navZoomMenu: HTMLElement | null = null;
  private lastZoomPct = -1;
  /**
   * Zoom elegido desde el menú (50/70/100 o uno escrito a mano). Queda como
   * preferido hasta que se vuelve a hacer zoom in/out manual (rueda/pellizco
   * o atajo), que lo limpia. `Ctrl+0` vuelve a él (o a 100% si no hay).
   */
  private preferredZoom: number | null = loadPreferredZoom();
  /** Zooms escritos a mano y guardados desde el menú. Persisten. */
  private customZooms: number[] = loadCustomZooms();
  private onDocPointerDown: ((e: PointerEvent) => void) | null = null;

  // Active toolbar tool. "select" is the default; "text" drops a text card at
  // the next click, then reverts to "select" (Figma-style).
  private tool: Tool = "select";
  private mode: Mode = "none";
  private lastSx = 0;
  private lastSy = 0;
  private moveLastWx = 0;
  private moveLastWy = 0;
  private selectedIds = new Set<string>();
  /**
   * Aristas seleccionadas. Va aparte de `selectedIds` a propósito: mezclarlas
   * obligaría a preguntar "¿este id es de tarjeta o de arista?" en cada uso.
   * Se puebla en la entrega 3; acá sólo alimenta el color de la capa.
   */
  private selectedEdgeIds = new Set<string>();
  private resizeId: string | null = null;
  private resizeDir: ResizeDir | null = null;
  /** Agarre bajo el puntero, para pintarlo lleno antes de que lo tomen. */
  private hoverResizeDir: ResizeDir | null = null;
  // Card rect captured when a resize gesture begins; anchors the opposite corner
  // and provides the aspect ratio for proportional (Shift) resizing.
  private resizeStart: { x: number; y: number; width: number; height: number } | null = null;
  // Whether the current move/resize gesture has already pushed an undo checkpoint.
  private gestureSnapshotted = false;
  /**
   * Grupo que se llevaría las tarjetas si el arrastre en curso terminara ahora.
   * Sólo vive mientras dura el gesto: alimenta el resaltado azul y decide la
   * reasignación al soltar.
   */
  private dropTargetId: string | null = null;
  /**
   * Grupo bajo el puntero, se resalte o no. `dropTargetId` sólo se pinta cuando
   * soltar cambiaría algo; éste es el que decide la reasignación al soltar.
   */
  private dropGroupId: string | null = null;
  private marqueeStart = { x: 0, y: 0 };
  private marqueeBase = new Set<string>();
  private spaceDown = false;
  private lastPointerSx = -1;
  private lastPointerSy = -1;
  private containerStartWx = 0;
  private containerStartWy = 0;
  private containerEndWx = 0;
  private containerEndWy = 0;
  /** Copiar, pegar y soltar archivos. */
  private clipboard!: CanvasClipboard;
  /** Aristas seleccionadas al empezar una marquesina con Shift. */
  private marqueeBaseEdges = new Set<string>();
  /**
   * Corrección del imán aplicada AHORA MISMO, respecto de donde estaría lo que
   * se mueve si sólo siguiera al puntero.
   *
   * Sin llevar esta cuenta el imán se acumula: cada frame vuelve a sumar la
   * corrección del anterior y lo arrastrado se va corriendo solo.
   */
  private snapDx = 0;
  private snapDy = 0;
  /** Renombrado inline de la etiqueta de un contenedor. */
  private groupRename!: InlineInput;
  /** Texto sobre los conectores, y su edición inline. */
  private edgeLabels = new EdgeLabels();
  private edgeLabelInput!: InlineInput;
  /** Barrita de puntas y ruteo sobre el conector seleccionado. */
  private edgeActions!: EdgeActions;
  // In-progress pencil stroke ([x0, y0, x1, y1, …] world coords), or null.
  private currentStroke: number[] | null = null;
  private inkDirty = false;
  // file nodes whose body has been (or is being) loaded — avoid re-fetching.
  private fileRequested = new Set<string>();
  /**
   * Cuerpos de notas ya leídos que esperan entrar al store, por id de nodo. Se
   * vuelcan juntos una vez por frame (ver `flushLoadedFiles`).
   */
  private loadedFiles = new Map<string, string>();
  private loadedFilesFrame: number | null = null;
  /** Nodos de texto ya medidos contra su caja. Ver `ensureTextFits`. */
  private textFitted = new Set<string>();

  // Observes the host element so the canvas re-fits when it changes size
  // without a window resize (e.g. the sidebar collapsing/expanding).
  private resizeObserver: ResizeObserver | null = null;

  /**
   * Reposo: con el lienzo quieto no hay nada que redibujar, y sin esto el
   * ticker corría `syncViews` y un render completo de Pixi a la frecuencia de
   * la pantalla (120 Hz en un Mac) para siempre. Pasado `IDLE_MS` sin nada que
   * lo despierte, el ticker se detiene; `wake()` lo vuelve a arrancar. Lo
   * despierta todo lo que puede cambiar lo que se ve: input del usuario, un
   * cambio del documento, una textura que terminó de rasterizarse, un resize y
   * las fuentes que terminan de cargar.
   */
  private lastWake = 0;
  private sleeping = false;
  private disposed = false;
  private unsubscribeStore: (() => void) | null = null;

  // Bound window listeners, kept so dispose() can remove them.
  private onWinPointerUp = () => this.onPointerUp();
  private onKeyDown!: (e: KeyboardEvent) => void;
  private onKeyUp!: (e: KeyboardEvent) => void;

  constructor(
    private host: HTMLElement,
    private store: StoreApi<DocState>,
    private options: CanvasViewOptions = {},
  ) {}

  async init(): Promise<void> {
    this.buildDom();
    this.edgeLabelInput = new InlineInput(this.root, {
      className: "edge-label-input",
      onCommit: (id, text) => {
        const edge = this.store.getState().doc.edges.find((e) => e.id === id);
        if (!edge || text === (edge.label ?? "")) return;
        this.store.getState().snapshot();
        this.store.getState().updateEdge(id, { label: text || undefined });
      },
      anchorOf: (id) => {
        const mid = this.edgeLayer.midpointOf(id);
        return mid ? this.camera.worldToScreen(mid.x, mid.y) : null;
      },
    });
    this.groupRename = new InlineInput(this.root, {
      className: "group-name-input",
      // El mismo aire y el mismo anclaje que la etiqueta, y su misma escala:
      // el editor tiene que ocupar el lugar exacto del nombre que tapa.
      offsetY: -GROUP_LABEL_GAP_PX,
      anchor: "translateY(-100%)",
      scaleOf: (id) => (this.currentCard(id)?.titleAutoScale ? 1 : this.camera.zoom),
      // Medidas de la tipografía de 16 px con la que se dibuja el nombre.
      width: { perChar: 8.8, min: 104 },
      onCommit: (id, name) => {
        const card = this.currentCard(id);
        if (!card || !name || name === card.name) return;
        this.store.getState().snapshot();
        this.store.getState().updateCard(id, { name });
      },
      anchorOf: (id) => {
        const card = this.currentCard(id);
        return card ? this.camera.worldToScreen(card.x, card.y) : null;
      },
    });
    this.cache.loadImage = this.options.loadBinary ?? null;
    this.cache.loadHighlights = this.options.loadHighlights ?? null;

    await this.app.init({
      canvas: this.canvas,
      resizeTo: this.host,
      background: UI_PAPER,
      antialias: true,
      // Sólo WebGPU, sin caída a WebGL: pasada como lista, Pixi no prueba
      // otro renderer, y donde no hay WebGPU el init falla en vez de seguir
      // en silencio con otro motor.
      preference: ["webgpu"],
      resolution: this.dpr,
      autoDensity: true,
    });

    this.camera = new Camera(this.world);

    this.app.stage.addChild(this.world);

    // Orden de apilado del mundo, de abajo hacia arriba. Las tarjetas se
    // anexan después (en `syncViews`) y la tinta se re-anexa al final de cada
    // sync para quedar siempre arriba de todo.
    this.groupsLayer.eventMode = "none";
    this.world.addChild(this.groupsLayer);
    this.world.addChild(this.edgeLayer.container);
    this.chromeLayer.eventMode = "none";
    this.inkLayer.eventMode = "none";
    this.world.addChild(this.chromeLayer, this.inkLayer);

    if (hudEnabled()) {
      this.hud = new DevHud(() => {
        const { cards, edges } = buildStressDiagram(this.store);
        console.info(`stress: +${cards} tarjetas, +${edges} aristas`);
        this.fitAll();
      });
      this.root.appendChild(this.hud.root);
    }

    this.marquee = new Graphics();
    this.marquee.eventMode = "none";
    this.marquee.visible = false;
    this.app.stage.addChild(this.marquee);

    this.guides = new Graphics();
    this.guides.eventMode = "none";
    this.app.stage.addChild(this.guides);
    this.clipboard = new CanvasClipboard({
      store: this.store,
      pastePoint: () => {
        const sx = this.lastPointerSx >= 0 ? this.lastPointerSx : this.app.screen.width / 2;
        const sy = this.lastPointerSy >= 0 ? this.lastPointerSy : this.app.screen.height / 2;
        return this.camera.screenToWorld(sx, sy);
      },
      selectedCards: () => this.selectedIds,
      selectedEdges: () => this.selectedEdgeIds,
      setSelection: (cards, edges) => {
        this.selectedIds = new Set(cards);
        this.selectedEdgeIds = new Set(edges);
      },
      isEditingText: () => this.isEditingText(),
      saveImage: this.options.saveImage,
    });
    this.connect = new ConnectGesture({
      store: this.store,
      camera: this.camera,
      edgeLayer: this.edgeLayer,
      cardAt: (wx, wy) => {
        const card = this.cardAt(wx, wy);
        return card?.type === "comment" ? null : card;
      },
      cardById: (id) => this.currentCard(id),
      color: () => this.toolbar.colorFor("connector"),
      edgeStyle: () => ({
        route: this.toolbar.route,
        fromEnd: this.toolbar.fromEnd,
        toEnd: this.toolbar.toEnd,
      }),
      onCreated: (edgeId) => {
        this.selectEdge(edgeId);
        // Como el texto y el contenedor, la herramienta vuelve a select tras un uso.
        if (this.tool === "connector") this.setTool("select");
      },
      onBegin: () => this.select(null),
      onDropInEmpty: (wx, wy, fromNodeId, fromSide) =>
        this.createConnectedNode(wx, wy, fromNodeId, fromSide),
    });
    // Los puertos son de PANTALLA (miden lo mismo en cualquier zoom), así que
    // van sobre el stage y no adentro del mundo, igual que la marquesina.
    this.app.stage.addChild(this.connect.ports.graphics);

    const cam = this.store.getState().doc.camera;
    this.camera.set(cam.x, cam.y, cam.zoom);

    this.bindEvents();

    // Pixi's resizeTo only reacts to window resizes; the host also changes width
    // when the sidebar collapses/expands (pure CSS, no window resize). Observe it
    // so the renderer re-fits instead of leaving a gap the width of the sidebar.
    this.resizeObserver = new ResizeObserver(() => {
      this.wake();
      this.app.resize();
      this.settleKeepVisible();
    });
    this.resizeObserver.observe(this.host);

    // Paint a complete first frame synchronously, then reveal the canvas —
    // otherwise the uncleared (black) GL buffer is visible for an instant.
    this.lastWake = performance.now();
    this.frame();
    this.app.render();
    this.canvas.style.visibility = "";

    this.app.ticker.add(this.frame);

    // Captura y pasivo: el reposo no tiene que enterarse tarde de un evento que
    // otro handler frena con `stopPropagation`, ni demorar el scroll.
    for (const type of WAKE_EVENTS) {
      window.addEventListener(type, this.wake, { capture: true, passive: true });
    }
    document.fonts?.addEventListener("loadingdone", this.wake);
    this.unsubscribeStore = this.store.subscribe(this.wake);
    this.cache.onReady = this.wake;
  }

  /** Saca al lienzo del reposo (ver `lastWake`). Barato: se llama por evento. */
  private wake = (): void => {
    if (this.disposed) return;
    this.lastWake = performance.now();
    if (this.sleeping) {
      this.sleeping = false;
      this.app.ticker.start();
    }
  };

  /** Detiene el ticker si nada pidió un frame en un rato y no hay un gesto en curso. */
  private sleepIfIdle(): void {
    if (this.mode !== "none" || this.inkDirty) return;
    if (performance.now() - this.lastWake < IDLE_MS) return;
    this.sleeping = true;
    // El render de Pixi de ESTE frame igual corre: `update()` recorre todos los
    // listeners aunque el ticker se detenga en el medio.
    this.app.ticker.stop();
  }

  private buildDom(): void {
    this.root = document.createElement("div");
    this.root.className = "canvas-view";

    this.canvas = document.createElement("canvas");
    this.canvas.className = "stage";
    // Hidden until init() paints the first frame: a fresh WebGPU buffer is
    // opaque black and would flash before the first render clears it.
    this.canvas.style.visibility = "hidden";

    this.toolbar = new CanvasToolbar({
      onTool: (tool) => this.useTool(tool),
      onColor: (hex) => this.recolorSelection(hex),
      // Elegir una forma del desplegable deja la herramienta lista para usarla.
      onShape: () => this.useTool("shape"),
      onTextAlign: (align) => this.restyleTextSelection({ align }),
      onTextBackground: (background) => this.restyleTextSelection({ background }),
      onTextColor: (color) => this.restyleTextSelection({ color }),
      onCommentPinned: (pinned) => this.restyleCommentSelection({ pinned }),
      onCommentBodyAutoScale: (bodyAutoScale) =>
        this.restyleCommentSelection({ bodyAutoScale }),
      onContainerTitleAutoScale: (titleAutoScale) =>
        this.restyleGroupSelection({ titleAutoScale }),
    });
    this.edgeActions = new EdgeActions({
      onEnds: (fromEnd, toEnd) => this.patchSelectedEdges({ fromEnd, toEnd }),
      onRoute: (route) => this.patchSelectedEdges({ route }),
      onLabel: () => this.startSelectedEdgeLabelEdit(),
    });
    this.cardActions = new CardActions({
      onFullView: (path) => this.options.onOpenFile?.(path, { peek: false }),
    });

    this.navControls = this.buildNavControls();
    this.root.append(
      this.canvas,
      this.textLabels.root,
      this.commentLayer.root,
      this.groupLabels.root,
      this.edgeLabels.root,
      this.edgeActions.root,
      this.cardActions.root,
      this.navControls,
      this.toolbar.root,
    );
    this.host.appendChild(this.root);
    this.setTool("select");
  }

  /** Elige forma y herramienta de una: es lo que hacen `R` y `O`. */
  private setShapeTool(kind: ShapeKind): void {
    this.select(null);
    this.toolbar.setShape(kind);
    this.setTool("shape");
  }

  /**
   * Cambio de herramienta iniciado por el usuario (click en la barra o atajo).
   * Suelta la selección: seguir mostrando el marco y las barritas sobre algo
   * que ya no se va a usar interrumpe el flujo (se probó mantenerla y molesta).
   * Los retornos internos a `select` (tras crear forma/contenedor/conector o
   * cerrar un editor) siguen usando `setTool` para no borrar lo recién creado.
   */
  private useTool(tool: Tool): void {
    this.select(null);
    this.setTool(tool);
  }

  private setTool(tool: Tool): void {
    this.tool = tool;
    this.toolbar.setTool(tool);
    this.canvas.style.cursor = toolCursor(tool);
    // Al salir del conector no queda ningún puerto colgado hasta el próximo
    // movimiento: se olvida el hover ahora mismo. `connect` aún no existe
    // cuando `buildDom` llama acá por primera vez, de ahí el `?.`.
    if (tool !== "connector") this.connect?.clearHover();
    this.syncColorPalette();
  }

  /**
   * Qué opciones ofrece la barra ahora mismo.
   *
   * El color importa cuando se va a crear algo que lo tenga (lápiz, contenedor,
   * conector, forma) o cuando hay algo seleccionado que se pueda repintar. La
   * alineación y el fondo, cuando se va a escribir o hay un texto seleccionado
   * al que aplicárselos.
   */
  private syncColorPalette(): void {
    const cards = this.store.getState().doc.cards;
    const selectedTexts = cards.some(
      (c) => c.type === "text" && this.selectedIds.has(c.id),
    );
    const selectedShapes = cards.some(
      (c) => c.type === "shape" && this.selectedIds.has(c.id),
    );
    const selectedComments = cards.some(
      (c) => c.type === "comment" && this.selectedIds.has(c.id),
    );
    const selectedGroups = cards.some(
      (c) => c.type === "group" && this.selectedIds.has(c.id),
    );
    // A quién le habla la fila de alineación. El texto suelto gana cuando hay
    // de los dos —una selección mixta se alinea igual entera—; la figura, sólo
    // cuando es lo único que hay en juego.
    const wantsText = this.tool === "text" || selectedTexts;
    const alignTarget = wantsText
      ? "text"
      : this.tool === "shape" || selectedShapes
        ? "shape"
        : "none";
    this.toolbar.syncOptions(
      this.tool === "draw" ||
        this.tool === "container" ||
        this.tool === "connector" ||
        this.tool === "shape" ||
        this.selectedEdgeIds.size > 0 ||
        cards.some(
          (c) => (c.type === "group" || c.type === "shape") && this.selectedIds.has(c.id),
        ),
      wantsText,
      alignTarget,
      this.tool === "comment" || selectedComments,
      this.tool === "container" || selectedGroups,
    );
    this.adoptSelectionStyle(cards);
  }

  /**
   * Con UN ítem seleccionado, la barra pasa a decir lo que ESE ítem es.
   *
   * Cada herramienta recuerda sus opciones, pero seleccionar algo también
   * cuenta como elegirlas: si hay un rectángulo verde a la vista, la barra no
   * puede seguir marcando el naranja que quedó guardado. Sería mentir dos
   * veces —el anillo diría un color que no es el de la forma, y tocar
   * cualquier otro la repintaría desde un punto de partida falso—, así que
   * gana el último ítem seleccionado y con eso nacen las próximas creaciones
   * de esa herramienta.
   *
   * Vale para todo lo que la barra ofrece: el estilo del texto; la forma y su
   * relleno; el color del contenedor; y el color, la ruta y las puntas del
   * conector.
   *
   * Sólo con UNO: con varios de estilos distintos no hay nada que adoptar sin
   * inventarlo. Corre por frame, y cada `set*` de la barra sale sin tocar el
   * DOM cuando el valor ya es ése.
   */
  private adoptSelectionStyle(cards: readonly Card[]): void {
    if (this.selectedIds.size === 0 && this.selectedEdgeIds.size === 1) {
      const edge = this.store.getState().doc.edges.find((e) => this.selectedEdgeIds.has(e.id));
      if (!edge) return;
      this.toolbar.setColorFor("connector", edge.color ?? DEFAULT_EDGE_COLOR);
      this.toolbar.setRoute(edge.route ?? DEFAULT_ROUTE);
      this.toolbar.setEdgeEnds(edge.fromEnd ?? DEFAULT_FROM_END, edge.toEnd ?? DEFAULT_TO_END);
      return;
    }
    if (this.selectedIds.size !== 1 || this.selectedEdgeIds.size > 0) return;
    const only = cards.find((c) => this.selectedIds.has(c.id));
    if (only?.type === "text" && only.text) {
      this.toolbar.setAlign(only.text.align);
      this.toolbar.setTextBackground(only.text.background);
      this.toolbar.setTextColor(only.text.color);
    } else if (only?.type === "shape" && only.shape) {
      this.toolbar.setShape(only.shape.kind);
      this.toolbar.setColorFor("shape", only.shape.fill);
      this.toolbar.setShapeAlign(only.shape.align ?? "center");
    } else if (only?.type === "group") {
      // Un contenedor sin color propio se dibuja con el de la herramienta
      // (ver `syncViews`): ahí no hay nada que adoptar, ya coinciden. El
      // tamaño del título, en cambio, se adopta siempre: no tiene "ausente".
      if (only.color) this.toolbar.setColorFor("container", only.color);
      this.toolbar.setContainerTitleAutoScale(only.titleAutoScale === true);
    } else if (only?.type === "comment" && only.comment) {
      this.toolbar.setCommentPinned(only.comment.pinned);
      this.toolbar.setCommentBodyAutoScale(only.comment.bodyAutoScale);
    }
  }

  /** Fija/libera el cuerpo o activa su compensación de zoom. */
  private restyleCommentSelection(patch: { pinned?: boolean; bodyAutoScale?: boolean }): void {
    const store = this.store.getState();
    const comments = store.doc.cards.filter(
      (card) =>
        card.type === "comment" &&
        this.selectedIds.has(card.id) &&
        card.comment !== undefined &&
        ((patch.pinned !== undefined && card.comment.pinned !== patch.pinned) ||
          (patch.bodyAutoScale !== undefined &&
            card.comment.bodyAutoScale !== patch.bodyAutoScale)),
    );
    if (comments.length === 0) return;
    store.snapshot();
    for (const card of comments) {
      store.updateCard(card.id, {
        comment: { ...card.comment!, ...patch },
        updatedAt: Date.now(),
      });
    }
  }

  /** Clava el título del contenedor al tamaño de pantalla, o lo suelta. */
  private restyleGroupSelection(patch: { titleAutoScale: boolean }): void {
    const store = this.store.getState();
    const groups = store.doc.cards.filter(
      (card) =>
        card.type === "group" && this.selectedIds.has(card.id) &&
        (card.titleAutoScale === true) !== patch.titleAutoScale,
    );
    if (groups.length === 0) return;
    store.snapshot();
    for (const card of groups) {
      store.updateCard(card.id, {
        // `undefined` y no `false`: el valor por defecto no se guarda, igual
        // que el resto de los estilos que se omiten al serializar.
        titleAutoScale: patch.titleAutoScale || undefined,
        updatedAt: Date.now(),
      });
    }
  }

  /**
   * Controles de navegación (esquina superior derecha): porcentaje de zoom en
   * vivo (clickeable: abre el menú 50/70/100 + personalizado) y botón de
   * autoenfoque para volver a la zona de trabajo si te perdés navegando.
   * Misma pastilla flotante que el toolbar del canvas.
   */
  private buildNavControls(): HTMLElement {
    const div = document.createElement("div");
    div.className = "nav-controls";

    const zoom = document.createElement("button");
    zoom.type = "button";
    zoom.className = "nav-zoom";
    zoom.title = "Current zoom level — click to choose";
    zoom.setAttribute("aria-haspopup", "listbox");
    zoom.setAttribute("aria-expanded", "false");
    zoom.textContent = "100%";
    zoom.addEventListener("pointerdown", (e) => e.stopPropagation());
    zoom.addEventListener("click", (e) => {
      e.stopPropagation();
      this.toggleZoomMenu();
    });
    this.navZoom = zoom;

    const focus = document.createElement("button");
    focus.className = "nav-auto-focus";
    focus.innerHTML = FOCUS_SVG;
    focus.title = "Autofocus: back to the work area (F)";
    focus.addEventListener("pointerdown", (e) => e.preventDefault());
    focus.addEventListener("click", () => this.focusWorkArea());

    div.append(zoom, focus);
    return div;
  }

  // ---- menú de zoom -------------------------------------------------------

  private toggleZoomMenu(): void {
    if (this.navZoomMenu) this.closeZoomMenu();
    else this.openZoomMenu();
  }

  private openZoomMenu(): void {
    this.closeZoomMenu();
    const menu = document.createElement("div");
    menu.className = "nav-zoom-menu";
    menu.setAttribute("role", "listbox");
    menu.setAttribute("aria-label", "Choose zoom");
    menu.addEventListener("pointerdown", (e) => e.stopPropagation());
    menu.addEventListener("click", (e) => e.stopPropagation());
    this.navZoomMenu = menu;
    this.renderZoomMenu();
    this.navControls.appendChild(menu);
    this.navZoom.setAttribute("aria-expanded", "true");

    this.onDocPointerDown = (e: PointerEvent) => {
      if (!menu.contains(e.target as Node) && e.target !== this.navZoom) {
        this.closeZoomMenu();
      }
    };
    // Captura para cerrar antes de que el canvas empiece un gesto.
    document.addEventListener("pointerdown", this.onDocPointerDown, true);

    const input = menu.querySelector<HTMLInputElement>(".nav-zoom-input");
    // Foco diferido: si se enfoca en el mismo tick del click, algunos
    // navegadores lo cierran por el propio evento que abrió el menú.
    requestAnimationFrame(() => input?.focus());
  }

  private closeZoomMenu(): void {
    this.navZoomMenu?.remove();
    this.navZoomMenu = null;
    this.navZoom?.setAttribute("aria-expanded", "false");
    if (this.onDocPointerDown) {
      document.removeEventListener("pointerdown", this.onDocPointerDown, true);
      this.onDocPointerDown = null;
    }
  }

  private zoomLabel(z: number): string {
    return `${Math.round(z * 100)}%`;
  }

  private isPreferred(z: number): boolean {
    return this.preferredZoom !== null && Math.abs(z - this.preferredZoom) < 1e-6;
  }

  private renderZoomMenu(): void {
    const menu = this.navZoomMenu;
    if (!menu) return;
    menu.innerHTML = "";

    const current = this.camera?.zoom ?? 1;
    const reference = this.preferredZoom ?? current;
    const customs = this.customZooms.filter((z) => !ZOOM_PRESETS.some((p) => Math.abs(p - z) < 1e-6));

    const addOption = (z: number, opts: { removable?: boolean } = {}) => {
      const active = Math.abs(z - reference) < 1e-6;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `nav-zoom-option${active ? " is-active" : ""}`;
      btn.setAttribute("role", "option");
      btn.setAttribute("aria-selected", active ? "true" : "false");
      const check = document.createElement("span");
      check.className = "nav-zoom-check";
      check.textContent = active ? "✓" : "";
      const label = document.createElement("span");
      label.textContent = this.zoomLabel(z);
      btn.append(check, label);
      if (opts.removable) {
        const rm = document.createElement("span");
        rm.className = "nav-zoom-remove";
        rm.title = "Forget this zoom";
        rm.textContent = "×";
        rm.addEventListener("click", (e) => {
          e.stopPropagation();
          this.customZooms = this.customZooms.filter((v) => Math.abs(v - z) > 1e-6);
          this.persistCustomZooms();
          if (this.isPreferred(z)) this.clearPreferredZoom();
          this.renderZoomMenu();
        });
        btn.appendChild(rm);
      }
      btn.addEventListener("click", () => this.applyZoomLevel(z, true));
      menu.appendChild(btn);
    };

    for (const z of ZOOM_PRESETS) addOption(z);
    for (const z of customs) addOption(z, { removable: true });

    const sep = document.createElement("div");
    sep.className = "nav-zoom-sep";
    menu.appendChild(sep);

    const form = document.createElement("form");
    form.className = "nav-zoom-custom";
    const input = document.createElement("input");
    input.className = "nav-zoom-input";
    input.type = "text";
    input.inputMode = "decimal";
    input.placeholder = "";
    input.setAttribute("aria-label", "Zoom personalizado en porcentaje");
    form.append(input);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const parsed = this.parseZoomInput(input.value);
      if (parsed === null) {
        input.classList.add("is-error");
        input.focus();
        return;
      }
      input.classList.remove("is-error");
      if (!this.customZooms.some((v) => Math.abs(v - parsed) < 1e-6)) {
        this.customZooms.push(parsed);
        this.customZooms.sort((a, b) => a - b);
        this.persistCustomZooms();
      }
      this.applyZoomLevel(parsed, true);
    });
    input.addEventListener("input", () => input.classList.remove("is-error"));
    menu.append(form);
  }

  /** Acepta "120", "120%", "1.2", "0,7" → factor de zoom, o null si inválido. */
  private parseZoomInput(raw: string): number | null {
    const t = raw.trim().replace("%", "").replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    if (!Number.isFinite(n) || n <= 0) return null;
    // <= 5 se interpreta como factor (1.2 = 120%); si no, como porcentaje.
    const factor = n <= 5 ? n : n / 100;
    const min = this.camera?.minZoom ?? 0.04;
    const max = this.camera?.maxZoom ?? 12;
    if (factor < min || factor > max) return null;
    return Math.round(factor * 1000) / 1000;
  }

  private persistPreferredZoom(): void {
    try {
      if (this.preferredZoom === null) localStorage.removeItem(PREF_ZOOM_KEY);
      else localStorage.setItem(PREF_ZOOM_KEY, String(this.preferredZoom));
    } catch {
      /* almacenamiento no disponible: el preferido vive sólo en memoria */
    }
  }

  private persistCustomZooms(): void {
    try {
      localStorage.setItem(CUSTOM_ZOOMS_KEY, JSON.stringify(this.customZooms));
    } catch {
      /* idem */
    }
  }

  /** Zoom elegido desde el menú: se aplica y queda como preferido. */
  private applyZoomLevel(zoom: number, fromMenu: boolean): void {
    if (!this.camera) return;
    const min = this.camera.minZoom;
    const max = this.camera.maxZoom;
    const target = Math.min(max, Math.max(min, zoom));
    const cx = this.app.screen.width / 2;
    const cy = this.app.screen.height / 2;
    const factor = target / this.camera.zoom;
    if (Math.abs(factor - 1) > 1e-6) {
      this.camera.zoomAt(factor, cx, cy);
      this.commitCamera();
    }
    if (fromMenu) {
      this.preferredZoom = target;
      this.persistPreferredZoom();
    }
    this.closeZoomMenu();
  }

  /** Zoom manual (rueda/pellizco/atajo): invalida el preferido del menú. */
  private clearPreferredZoom(): void {
    if (this.preferredZoom === null) return;
    this.preferredZoom = null;
    this.persistPreferredZoom();
  }

  /** Zoom centrado en la vista, usado por los atajos de zoom in/out. */
  private zoomByStep(factor: number): void {
    if (!this.camera) return;
    this.camera.zoomAt(factor, this.app.screen.width / 2, this.app.screen.height / 2);
    this.clearPreferredZoom();
    this.closeZoomMenu();
    this.commitCamera();
  }

  private resetZoom(): void {
    this.applyZoomLevel(this.preferredZoom ?? 1, false);
    // `applyZoomLevel` con fromMenu=false no toca el preferido: resetear no
    // lo borra, sólo vuelve a él (o a 100% si nunca se eligió uno).
  }

  /**
   * Autoenfoque: si hay tarjetas seleccionadas centra la vista en ellas
   * (manteniendo el zoom); si no, encuadra todas las tarjetas; si el canvas
   * está vacío, vuelve al origen con zoom 100%.
   */
  focusWorkArea(): void {
    const cards = this.store.getState().doc.cards;
    const selected = cards.filter((c) => this.selectedIds.has(c.id));
    const targets = selected.length > 0 ? selected : cards;
    if (targets.length === 0) {
      this.camera.set(this.app.screen.width / 2, this.app.screen.height / 2, 1);
      this.commitCamera();
      return;
    }
    if (selected.length > 0) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const c of targets) {
        minX = Math.min(minX, c.x);
        minY = Math.min(minY, c.y);
        maxX = Math.max(maxX, c.x + c.width);
        maxY = Math.max(maxY, c.y + c.height);
      }
      const cx = (minX + maxX) / 2;
      const cy = (minY + maxY) / 2;
      const zoom = this.camera.zoom;
      this.camera.set(
        this.app.screen.width / 2 - cx * zoom,
        this.app.screen.height / 2 - cy * zoom,
        zoom,
      );
      this.commitCamera();
      return;
    }
    this.fitAll();
  }

  /**
   * Elegir un color repinta lo seleccionado que tenga color propio —
   * contenedores, formas y conectores — además de dejarlo recordado para la
   * herramienta de cada tipo. Cada herramienta guarda el suyo: pintar una
   * forma de rojo no cambia el próximo lápiz, conector ni contenedor.
   */
  private recolorSelection(hex: string): void {
    const store = this.store.getState();
    const groups = store.doc.cards.filter(
      (c) => c.type === "group" && this.selectedIds.has(c.id) && c.color !== hex,
    );
    const shapes = store.doc.cards.filter(
      (c) => c.type === "shape" && this.selectedIds.has(c.id) && c.shape?.fill !== hex,
    );
    const edges = store.doc.edges.filter(
      (e) => this.selectedEdgeIds.has(e.id) && e.color !== hex,
    );
    if (groups.length === 0 && shapes.length === 0 && edges.length === 0) return;
    if (groups.length > 0) this.toolbar.setColorFor("container", hex);
    if (shapes.length > 0) this.toolbar.setColorFor("shape", hex);
    if (edges.length > 0) this.toolbar.setColorFor("connector", hex);
    store.transaction(() => {
      for (const g of groups) store.updateCard(g.id, { color: hex });
      for (const sh of shapes) {
        store.updateCard(sh.id, { shape: { ...sh.shape!, fill: hex } });
      }
      for (const e of edges) store.updateEdge(e.id, { color: hex });
    });
  }

  /**
   * Aplica la alineación, el fondo o el color elegidos a los nodos de texto
   * seleccionados, además de dejarlos como estilo de los que se creen después.
   *
   * Es el gemelo de `recolorSelection` para el otro estilo que ofrece la barra,
   * y funciona igual: si no hay nada seleccionado, sólo cambia el valor con el
   * que va a nacer el próximo texto.
   *
   * La alineación, además, va a las FIGURAS seleccionadas: adentro de una
   * figura también hay texto y es la misma pregunta, así que el mismo botón la
   * contesta para las dos cosas y una selección mixta se alinea entera de una
   * vez. El fondo y el color de letra no: esos son sólo del texto suelto (una
   * figura ya tiene relleno propio y su texto contrasta contra él).
   */
  private restyleTextSelection(patch: {
    align?: TextAlign;
    background?: string;
    color?: string;
  }): void {
    const store = this.store.getState();
    const texts = store.doc.cards.filter(
      (c) =>
        c.type === "text" &&
        this.selectedIds.has(c.id) &&
        c.text !== undefined &&
        ((patch.align !== undefined && c.text.align !== patch.align) ||
          (patch.background !== undefined && c.text.background !== patch.background) ||
          (patch.color !== undefined && c.text.color !== patch.color)),
    );
    const shapes =
      patch.align === undefined
        ? []
        : store.doc.cards.filter(
            (c) =>
              c.type === "shape" &&
              this.selectedIds.has(c.id) &&
              c.shape !== undefined &&
              c.shape.align !== patch.align,
          );
    if (texts.length === 0 && shapes.length === 0) return;
    store.transaction(() => {
      for (const card of texts) {
        const text = { ...card.text!, ...patch };
        // Alinear no cambia el tamaño; cambiar el fondo o el color tampoco. Se recalcula
        // igual por una sola razón: es la misma función que corre al escribir, y
        // tener un único lugar donde se decide el tamaño es lo que evita que dos
        // caminos discrepen.
        store.updateCard(card.id, {
          text,
          ...textNodeSize(text, card.width),
          updatedAt: Date.now(),
        });
      }
      for (const card of shapes) {
        // La figura no cambia de tamaño al alinear: su caja la puso quien la
        // dibujó, y el texto se acomoda adentro.
        store.updateCard(card.id, {
          shape: { ...card.shape!, align: patch.align! },
          updatedAt: Date.now(),
        });
      }
    });
  }

  // ---- public actions -----------------------------------------------------

  /**
   * Borra lo seleccionado, tarjetas y conectores, en UN solo paso de deshacer.
   *
   * La transacción no es un detalle: `ungroup` y `removeCards` toman cada uno su
   * checkpoint, así que borrar una selección mixta dejaba dos `⌘Z` para
   * deshacer una sola acción del usuario.
   */
  deleteSelected(): void {
    if (this.selectedIds.size === 0 && this.selectedEdgeIds.size === 0) return;
    if (this.textEditor.editingId && this.selectedIds.has(this.textEditor.editingId)) {
      this.textEditor.close();
    }
    if (this.commentLayer.editingId && this.selectedIds.has(this.commentLayer.editingId)) {
      this.commentLayer.closeEditor();
    }
    const store = this.store.getState();
    const cards = store.doc.cards;
    // Los contenedores se disuelven (sus hijos quedan); el resto se borra.
    const toDelete = new Set<string>();
    const toUngroup: string[] = [];
    for (const id of this.selectedIds) {
      const card = cards.find((c) => c.id === id);
      if (card?.type === "group") toUngroup.push(id);
      else toDelete.add(id);
    }
    const edges = new Set(this.selectedEdgeIds);
    store.transaction(() => {
      for (const id of toUngroup) store.ungroup(id);
      if (toDelete.size > 0) store.removeCards(toDelete);
      if (edges.size > 0) store.removeEdges(edges);
    });
    this.selectedIds.clear();
    this.selectedEdgeIds.clear();
  }

  /**
   * Los dos extremos de la arista seleccionada, en píxeles de pantalla, o null
   * si no hay exactamente una seleccionada y visible.
   */
  private selectedEdgeScreenEnds(): { x: number; y: number }[] | null {
    if (this.selectedEdgeIds.size !== 1) return null;
    const ends = this.edgeLayer.endpointsOf([...this.selectedEdgeIds][0]);
    if (!ends) return null;
    return ends.map((p) => this.camera.worldToScreen(p.x, p.y));
  }

  /** Aplica el mismo cambio a todas las aristas seleccionadas, en un paso.
   *  Si el cambio es de estilo (ruta o puntas), queda recordado: el próximo
   *  conector nace con eso, igual que ya pasaba con el color. */
  private patchSelectedEdges(patch: Partial<Omit<Edge, "id">>): void {
    if (this.selectedEdgeIds.size === 0) return;
    if (patch.route !== undefined) this.toolbar.setRoute(patch.route);
    if (patch.fromEnd !== undefined || patch.toEnd !== undefined) {
      const current = this.store.getState().doc.edges.find((e) => this.selectedEdgeIds.has(e.id));
      this.toolbar.setEdgeEnds(
        patch.fromEnd ?? current?.fromEnd ?? DEFAULT_FROM_END,
        patch.toEnd ?? current?.toEnd ?? DEFAULT_TO_END,
      );
    }
    const store = this.store.getState();
    const ids = [...this.selectedEdgeIds];
    store.transaction(() => {
      for (const id of ids) store.updateEdge(id, patch);
    });
  }

  /** Abre el editor de etiqueta del conector seleccionado (uno solo).
   *  Es lo mismo que hacían `Enter` y el doble click: el botón T de la
   *  barrita es la tercera puerta a este mismo input. */
  private startSelectedEdgeLabelEdit(): void {
    if (this.selectedEdgeIds.size !== 1) return;
    const id = [...this.selectedEdgeIds][0];
    const edge = this.store.getState().doc.edges.find((e) => e.id === id);
    if (edge && this.edgeLayer.isVisible(id)) this.edgeLabelInput.start(id, edge.label ?? "");
  }

  selectAll(): void {
    const { doc } = this.store.getState();
    this.selectedIds = new Set(doc.cards.map((c) => c.id));
    this.selectedEdgeIds = new Set(doc.edges.map((e) => e.id));
  }

  copySelected(): void {
    this.clipboard.copy();
  }

  pasteClipboard(): void {
    this.clipboard.paste();
  }

  fitAll(): void {
    const cards = this.store.getState().doc.cards;
    if (cards.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const c of cards) {
      minX = Math.min(minX, c.x);
      minY = Math.min(minY, c.y);
      maxX = Math.max(maxX, c.x + c.width);
      maxY = Math.max(maxY, c.y + c.height);
    }
    const pad = 80;
    const bw = maxX - minX + pad * 2;
    const bh = maxY - minY + pad * 2;
    const zoom = Math.min(this.app.screen.width / bw, this.app.screen.height / bh, this.camera.maxZoom);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    this.camera.set(
      this.app.screen.width / 2 - cx * zoom,
      this.app.screen.height / 2 - cy * zoom,
      zoom,
    );
    this.commitCamera();
  }

  // ---- grouping -----------------------------------------------------------

  private groupSelected(): void {
    if (this.selectedIds.size < 2) return;
    const store = this.store.getState();
    const groupId = store.groupCards(this.selectedIds);
    if (groupId) {
      this.selectedIds.clear();
      this.selectedIds.add(groupId);
    }
  }

  private ungroupSelected(): void {
    const cards = this.store.getState().doc.cards;
    for (const id of this.selectedIds) {
      const card = cards.find((c) => c.id === id);
      if (card?.type === "group") {
        const children = card.childrenIds ?? [];
        this.store.getState().ungroup(id);
        this.selectedIds = new Set(children);
        return;
      }
    }
  }

  // ---- nombre del grupo ----------------------------------------------------

  /**
   * Edición inline del nombre, sobre la etiqueta misma: Enter o click afuera
   * confirman, Escape descarta. Un nombre vacío vuelve al anterior — en Figma
   * un frame nunca queda sin nombre.
   */
  private createContainer(x: number, y: number, w: number, h: number): void {
    const store = this.store.getState();
    // Find cards that intersect the dragged rectangle
    const inside: string[] = [];
    for (const c of store.doc.cards) {
      if (c.type === "group") continue;
      if (c.x < x + w && c.x + c.width > x && c.y < y + h && c.y + c.height > y) {
        inside.push(c.id);
      }
    }
    const card = store.addCard({
      type: "group",
      x,
      y,
      width: w,
      height: h,
      markdown: "",
      childrenIds: inside,
      color: this.toolbar.colorFor("container"),
      titleAutoScale: this.toolbar.containerTitleAutoScale || undefined,
    });
    this.selectedIds.clear();
    this.selectedIds.add(card.id);
  }

  /**
   * Crea una forma con la geometría y el color activos, y abre su editor.
   *
   * El relleno es EL color de la paleta, sólido y sin borde; el texto encima se
   * pinta con el contraste que calcula `textColorFor`. Antes el relleno era un
   * tinte al 14% de ese color y el color elegido quedaba en el borde, así que
   * lo que se elegía nunca era lo que se veía (ver `docs/plan-formas.md` §1.5).
   */
  private createShape(x: number, y: number, w: number, h: number): void {
    const store = this.store.getState();
    let card!: Card;
    store.transaction(() => {
      card = store.addCard({
        type: "shape",
        x,
        y,
        // Nace vacía, así que sólo se le exigen los mínimos: el alto lo va a pedir
        // el texto a medida que se escriba. Exigirle más acá rompería el Shift,
        // que promete una caja de proporción 1:1.
        width: Math.max(SHAPE_MIN_W, w),
        height: Math.max(SHAPE_MIN_H, h),
        markdown: "",
        shape: {
          kind: this.toolbar.shape,
          fill: this.toolbar.colorFor("shape"),
          text: "",
          align: this.toolbar.shapeAlign,
        },
      });
      store.adoptIntoGroups([card.id]);
    });
    this.select(card.id);
    this.startShapeEdit(card, { isNewNode: true, selectToolOnClose: true });
  }

  /**
   * Soltar un conector en el vacío crea ahí una forma y la conecta, con el
   * editor abierto para escribir de una. Es el flujo que permite armar un
   * diagrama entero sin volver a la barra de herramientas.
   *
   * La salida respeta el lado elegido al arrastrar; la llegada se calcula por
   * posición y también queda explícita para que no se reacomode sola después.
   *
   * Todo va en UNA transacción: nodo y flecha se deshacen con un solo `⌘Z`.
   * Es exactamente para esto que existe `transaction`.
   */
  private createConnectedNode(
    wx: number,
    wy: number,
    fromNodeId: string,
    fromSide?: import("../model/types.ts").EdgeSide,
  ): void {
    const store = this.store.getState();
    const shapeColor = this.toolbar.colorFor("shape");
    const edgeColor = this.toolbar.colorFor("connector");
    const w = SHAPE_DEFAULT_W;
    const h = SHAPE_DEFAULT_H;
    let created: Card | undefined;
    let edgeId: string | null = null;

    store.transaction(() => {
      const card = store.addCard({
        type: "shape",
        // Centrada en el punto donde se soltó, que es donde está el puntero.
        x: wx - w / 2,
        y: wy - h / 2,
        width: w,
        height: h,
        markdown: "",
        shape: { kind: this.toolbar.shape, fill: shapeColor, text: "", align: this.toolbar.shapeAlign },
      });
      created = card;
      store.adoptIntoGroups([card.id]);
      const source = store.doc.cards.find((c) => c.id === fromNodeId);
      // La llegada mira hacia la salida según la posición relativa, y ambos
      // lados quedan guardados para que no se muevan solos después.
      const toSide = source
        ? autoSides(
            { x: source.x, y: source.y, width: source.width, height: source.height },
            { x: card.x, y: card.y, width: card.width, height: card.height },
          )[1]
        : undefined;
      edgeId =
        store.addEdge({
          fromNode: fromNodeId,
          toNode: card.id,
          ...(fromSide ? { fromSide } : {}),
          ...(toSide ? { toSide } : {}),
          color: edgeColor,
          route: this.toolbar.route,
          fromEnd: this.toolbar.fromEnd,
          toEnd: this.toolbar.toEnd,
        })?.id ?? null;
    });

    if (!created) return;
    if (!edgeId) {
      // Sin la arista, un nodo suelto no es lo que se pidió.
      store.discardCards([created.id]);
      return;
    }
    this.select(created.id);
    this.startShapeEdit(created, { isNewNode: true, selectToolOnClose: true });
  }

  /**
   * Duplica lo seleccionado y, si era un solo nodo, conecta la copia al
   * original: encadenar cajas es lo que más se repite armando un diagrama.
   */
  private duplicateSelected(): void {
    const store = this.store.getState();
    const originals = store.doc.cards.filter((c) => this.selectedIds.has(c.id));
    if (originals.length === 0) return;

    const now = Date.now();
    const remap = new Map<string, string>();
    for (const c of originals) remap.set(c.id, createId());
    const copies: Card[] = originals.map((c) => ({
      ...c,
      id: remap.get(c.id)!,
      x: c.x + DUPLICATE_OFFSET,
      y: c.y + DUPLICATE_OFFSET,
      childrenIds: c.childrenIds
        ?.map((childId) => remap.get(childId))
        .filter((v): v is string => !!v),
      updatedAt: now,
    }));

    store.transaction(() => {
      store.addCards(copies);
      // La copia de una tarjeta de adentro de un grupo cae, desplazada, adentro
      // del mismo grupo: pasa a ser suya. Las copias que ya tienen dueño —los
      // hijos de un contenedor duplicado— las saltea `adoptIntoGroups`.
      store.adoptIntoGroups(copies.map((c) => c.id));
      // Las aristas internas a la selección viajan con ella, igual que al pegar.
      const internal = store.doc.edges.filter(
        (e) => remap.has(e.fromNode) && remap.has(e.toNode),
      );
      if (internal.length > 0) {
        store.addEdges(
          internal.map((e) => ({
            ...e,
            id: createId("edge"),
            fromNode: remap.get(e.fromNode)!,
            toNode: remap.get(e.toNode)!,
            updatedAt: now,
          })),
        );
      }
      // Un solo nodo: la copia queda encadenada al original, con lados
      // explícitos para que no se reacomode sola después.
      if (originals.length === 1 && originals[0].type !== "comment") {
        const [fromSide, toSide] = autoSides(
          {
            x: originals[0].x,
            y: originals[0].y,
            width: originals[0].width,
            height: originals[0].height,
          },
          { x: copies[0].x, y: copies[0].y, width: copies[0].width, height: copies[0].height },
        );
        store.addEdge({
          fromNode: originals[0].id,
          toNode: copies[0].id,
          fromSide,
          toSide,
          color: this.toolbar.colorFor("connector"),
          route: this.toolbar.route,
          fromEnd: this.toolbar.fromEnd,
          toEnd: this.toolbar.toEnd,
        });
      }
    });
    this.selectedIds = new Set(copies.map((c) => c.id));
    this.selectedEdgeIds.clear();
  }

  // ---- interaction --------------------------------------------------------

  private bindEvents(): void {
    const c = this.canvas;
    c.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    c.addEventListener("pointermove", (e) => this.onPointerMove(e));
    c.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
    c.addEventListener("pointerleave", () => {
      if (this.mode !== "connect") this.connect.clearHover();
      this.commentLayer.hoverAt(-1, -1);
    });
    c.addEventListener("dblclick", (e) => this.onDoubleClick(e));
    window.addEventListener("pointerup", this.onWinPointerUp);

    // Accept drags of a vault file from the sidebar OR image files from the OS
    // (Finder/Explorer). Both create a node at the drop point.
    c.addEventListener("dragover", (e) => {
      const types = e.dataTransfer?.types;
      if (types?.includes(DND_PATH_TYPE) || types?.includes("Files")) {
        e.preventDefault();
        e.dataTransfer!.dropEffect = "copy";
      }
    });
    c.addEventListener("drop", (e) => this.onDrop(e));
    c.addEventListener("contextmenu", (e) => this.onContextMenu(e));

    this.onKeyDown = (e) => this.handleKeyDown(e);
    this.onKeyUp = (e) => this.handleKeyUp(e);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);

    // Clipboard goes through native copy/paste events (not the keydown handler)
    // so an OS-clipboard image and our internal card clipboard share one path
    // and can't both fire on the same ⌘V.
    window.addEventListener("copy", this.clipboard.onCopy);
    window.addEventListener("paste", this.clipboard.onPaste);
  }

  /**
   * ¿Manda este lienzo sobre esta tecla?
   *
   * No, por dos motivos distintos: el foco está en un campo o editor (ahí manda
   * el campo — copiar adentro de un campo no debe copiar las tarjetas), o está
   * en otra parte del workspace, que es DOM que este lienzo no conoce.
   */
  private ownsKeyboard(target: EventTarget | null): boolean {
    if (this.options.hasKeyboard?.() === false) return false;
    return !isTextEntryTarget(target);
  }

  /** True when focus is inside any text field or editor (let it handle
   *  keys/clipboard: copiar dentro de un campo no debe copiar las tarjetas). */
  private isEditingText(): boolean {
    return !this.ownsKeyboard(document.activeElement);
  }

  private handleKeyDown(e: KeyboardEvent): void {
    // Escribiendo en cualquier campo o editor —o trabajando del otro lado del
    // workspace—: los atajos del canvas (espacio, borrar, herramientas) no
    // deben robar las teclas.
    if (!this.ownsKeyboard(e.target)) return;

    if (e.code === "Space" && !this.spaceDown) {
      this.spaceDown = true;
      if (this.mode === "none") this.canvas.style.cursor = "grab";
      e.preventDefault();
      return;
    }

    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (mod) {
      // Copy/paste are handled by native clipboard events (see onCopy/onPaste).
      if (key === "z") {
        e.preventDefault();
        if (e.shiftKey) this.store.getState().redo();
        else this.store.getState().undo();
        this.afterHistoryChange();
      } else if (key === "y") {
        e.preventDefault();
        this.store.getState().redo();
        this.afterHistoryChange();
      } else if (key === "a") {
        e.preventDefault();
        this.selectAll();
      } else if (key === "d") {
        e.preventDefault();
        this.duplicateSelected();
      } else if (key === "g") {
        e.preventDefault();
        if (e.shiftKey) this.ungroupSelected();
        else this.groupSelected();
      } else if (e.key === "+" || e.key === "=") {
        // Zoom in manual (limpia el preferido del menú).
        e.preventDefault();
        this.zoomByStep(1.2);
      } else if (e.key === "-") {
        // Zoom out manual (limpia el preferido del menú).
        e.preventDefault();
        this.zoomByStep(1 / 1.2);
      } else if (e.key === "0") {
        // Vuelve al preferido (o a 100% si nunca se eligió uno).
        e.preventDefault();
        this.resetZoom();
      }
      return;
    }
    if (e.key === "Escape" && this.navZoomMenu) {
      this.closeZoomMenu();
      return;
    }
    if (e.key === "Enter" && this.selectedEdgeIds.size === 1) {
      e.preventDefault();
      this.startSelectedEdgeLabelEdit();
      return;
    }
    if (e.key === "Delete" || e.key === "Backspace") this.deleteSelected();
    else if (key === "h") this.useTool("pan");
    else if (key === "t") this.useTool("text");
    else if (key === "m") this.useTool("comment");
    else if (key === "p") this.useTool("draw");
    // `C` es conector, como en Figma. El contenedor vivía en `C` hasta que
    // aparecieron los conectores y se mudó a `A`, que en Figma también abre la
    // herramienta Frame: `F` ya estaba tomada acá por el autoenfoque.
    else if (key === "c") this.useTool("connector");
    else if (key === "a") this.useTool("container");
    else if (key === "f") this.focusWorkArea();
    else if (key === "r") this.setShapeTool("rect");
    else if (key === "o") this.setShapeTool("ellipse");
    else if (key === "d") this.setShapeTool("diamond");
    else if (key === "v" || e.key === "Escape") {
      if (this.mode === "connect") {
        this.connect.cancel();
        this.mode = "none";
      }
      this.useTool("select");
    }
  }

  /**
   * Sin guarda de foco, a propósito: si el espacio se apretó con el lienzo al
   * mando y el foco se fue mientras tanto, soltarlo tiene que limpiar
   * `spaceDown` igual — filtrarlo acá lo dejaría trabado en modo mano.
   */
  private handleKeyUp(e: KeyboardEvent): void {
    if (e.code !== "Space") return;
    this.spaceDown = false;
    if (this.mode !== "pan") this.canvas.style.cursor = toolCursor(this.tool);
  }

  /** After an undo/redo, drop selection/editing that points at cards that no
   *  longer exist in the restored document. */
  private afterHistoryChange(): void {
    const { doc } = this.store.getState();
    const ids = new Set(doc.cards.map((c) => c.id));
    for (const id of [...this.selectedIds]) {
      if (!ids.has(id)) this.selectedIds.delete(id);
    }
    const edgeIds = new Set(doc.edges.map((e) => e.id));
    for (const id of [...this.selectedEdgeIds]) {
      if (!edgeIds.has(id)) this.selectedEdgeIds.delete(id);
    }
    if (this.textEditor.editingId && !ids.has(this.textEditor.editingId)) {
      this.textEditor.close();
    }
    if (this.commentLayer.editingId && !ids.has(this.commentLayer.editingId)) {
      this.commentLayer.closeEditor();
    }
  }

  private onDrop(e: DragEvent): void {
    // Image files dragged from the OS (Finder/Explorer) → save into the vault.
    const imageFiles = e.dataTransfer
      ? [...e.dataTransfer.files].filter(
          (f) => f.type.startsWith("image/") || f.type === "application/pdf",
        )
      : [];
    if (imageFiles.length > 0 && this.options.saveImage) {
      e.preventDefault();
      void this.clipboard.addImages(
        imageFiles,
        this.camera.screenToWorld(e.offsetX, e.offsetY),
      );
      return;
    }

    // A vault file dragged from the sidebar → embed it as a `file` node.
    const path = e.dataTransfer?.getData(DND_PATH_TYPE);
    if (!path) return;
    e.preventDefault();
    void this.addVaultFile(path, this.camera.screenToWorld(e.offsetX, e.offsetY));
  }

  /**
   * Embed a vault file dropped from the sidebar. A PDF is sized from its cover
   * so the card matches the page's proportions instead of the generic note box.
   */
  private async addVaultFile(path: string, at: { x: number; y: number }): Promise<void> {
    let width = 320;
    let height = 240;
    if (isPdfPath(path) && this.options.loadBinary) {
      try {
        const size = await pdfCoverSize(await this.options.loadBinary(path));
        width = 360;
        height = Math.max(80, Math.round((width * size.height) / Math.max(1, size.width)));
      } catch (err) {
        // Un PDF ilegible se embebe igual, con la caja por defecto.
        console.error("pdf: no se pudo medir la portada", path, err);
      }
    }
    const store = this.store.getState();
    let card!: Card;
    store.transaction(() => {
      card = store.addCard({
        type: "file",
        file: path,
        markdown: "",
        x: at.x - width / 2,
        y: at.y - height / 2,
        width,
        height,
      });
      store.adoptIntoGroups([card.id]);
    });
    this.select(card.id);
  }

  /**
   * Grupo cuyo nombre cae bajo el punto. La etiqueta vive fuera del recuadro
   * (arriba del borde superior), así que se testea aparte y ANTES que todo:
   * como en Figma, el nombre siempre es clickeable aunque quede sobre otra
   * tarjeta.
   */
  private groupLabelAt(worldX: number, worldY: number): Card | null {
    const cards = this.store.getState().doc.cards;
    // La etiqueta es DOM y se mide en píxeles de pantalla, así que el punto de
    // mundo vuelve a pantalla para testearla.
    const p = this.camera.worldToScreen(worldX, worldY);
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type !== "group") continue;
      if (this.groupLabels.hit(c.id, p.x, p.y)) return c;
    }
    return null;
  }

  /**
   * La tarjeta NO contenedor de más arriba bajo el punto, o null si ahí sólo
   * hay hueco (o contenedor, que para esto es lo mismo).
   *
   * Es la respuesta a "¿esto es algo de adentro del grupo?", y la consultan
   * tres lugares que TIENEN que estar de acuerdo: el hit-test, la banda de
   * resize del contenedor y el escape de Cmd/Ctrl. Cuando no lo estaban, el
   * cursor prometía una cosa y el click hacía otra.
   */
  private childAt(worldX: number, worldY: number): Card | null {
    const cards = this.store.getState().doc.cards;
    const screen = this.camera.worldToScreen(worldX, worldY);
    const commentId = this.commentLayer.hitAt(screen.x, screen.y);
    if (commentId) return cards.find((card) => card.id === commentId) ?? null;
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type === "group") continue;
      if (c.type === "comment") continue; // usa su geometría DOM real, arriba
      // Una forma se prueba contra su geometría de verdad; el resto, contra su
      // rectángulo. Ver `hitsShape`.
      if (c.type === "shape") {
        if (hitsShape(c.shape?.kind ?? "rect", c.x, c.y, c.width, c.height, worldX, worldY)) {
          return c;
        }
        continue;
      }
      if (worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height) {
        return c;
      }
    }
    return null;
  }

  /**
   * El contenedor al que sube Cmd/Ctrl+click: el dueño de `child` si lo tiene,
   * y si no el de más arriba cuyo rect contenga el punto —así el escape también
   * sirve parado en un hueco de adentro, no sólo encima de una tarjeta.
   *
   * Sobre un contenedor no devuelve nada: no anidamos grupos, así que no hay
   * nivel al que subir.
   */
  private groupToEscalate(worldX: number, worldY: number, child: Card | null): Card | null {
    if (child?.type === "group") return null;
    const cards = this.store.getState().doc.cards;
    if (child) {
      const owner = cards.find(
        (c) => c.type === "group" && (c.childrenIds ?? []).includes(child.id),
      );
      if (owner) return owner;
    }
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type !== "group") continue;
      if (worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height) {
        return c;
      }
    }
    return null;
  }

  /** La tarjeta bajo el punto, sin considerar aristas. */
  private cardAt(worldX: number, worldY: number): Card | null {
    const label = this.groupLabelAt(worldX, worldY);
    if (label) return label;
    const child = this.childAt(worldX, worldY);
    if (child) return child;
    const cards = this.store.getState().doc.cards;
    // Second pass: group cards (hit-tested last, so clicking a child selects the child)
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type !== "group") continue;
      if (worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height) {
        return c;
      }
    }
    return null;
  }

  /**
   * Qué hay bajo el punto: una tarjeta, una arista, o nada.
   *
   * El orden importa y es una decisión de producto: etiqueta de contenedor →
   * tarjetas → **aristas** → contenedores. O sea que una flecha que pasa por
   * encima de una tarjeta NO le roba el click; en el vacío, sí.
   */
  private hitTest(worldX: number, worldY: number): Hit | null {
    const label = this.groupLabelAt(worldX, worldY);
    if (label) return { kind: "card", card: label };

    const cards = this.store.getState().doc.cards;
    const commentScreen = this.camera.worldToScreen(worldX, worldY);
    const commentId = this.commentLayer.hitAt(commentScreen.x, commentScreen.y);
    if (commentId) {
      const comment = cards.find((card) => card.id === commentId);
      if (comment) return { kind: "card", card: comment };
    }
    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type === "group" || c.type === "comment") continue;
      if (worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height) {
        return { kind: "card", card: c };
      }
    }

    // La etiqueta es DOM y se mide en píxeles de pantalla, así que el punto de
    // mundo vuelve a pantalla para testearla. Va antes que la línea: el texto
    // puede quedar más lejos del trazo que la tolerancia de click.
    const sp = this.camera.worldToScreen(worldX, worldY);
    for (const e of this.store.getState().doc.edges) {
      if (e.label && this.edgeLabels.hit(e.id, sp.x, sp.y)) return { kind: "edge", edge: e };
    }

    const edgeId = this.edgeLayer.hitTest(worldX, worldY, EDGE_HIT_PX / this.camera.zoom);
    if (edgeId) {
      const edge = this.store.getState().doc.edges.find((e) => e.id === edgeId);
      if (edge) return { kind: "edge", edge };
    }

    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type !== "group") continue;
      // El CUERPO del contenedor no se agarra. Un hueco entre dos tarjetas es
      // "adentro del grupo", no "el grupo": devolverlo ahí era lo que hacía
      // imposible lazar las tarjetas de adentro —todo arrastre en el hueco
      // movía el contenedor entero— y lo que se lo llevaba puesto a cada rato.
      //
      // Queda agarrable por su cromo: la etiqueta (ya probada arriba de todo),
      // la banda de su borde, y su interior sólo si YA está seleccionado, para
      // poder seguir empujándolo desde el medio una vez tomado.
      //
      // Ojo con el `inside`: la excepción del seleccionado sigue siendo una
      // regla de POSICIÓN. Sin él, un grupo seleccionado se quedaba con el
      // click en cualquier punto del canvas —incluido el vacío de afuera— y no
      // había forma de deseleccionarlo.
      const inside =
        worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height;
      if (this.onGroupBand(c, worldX, worldY) || (inside && this.selectedIds.has(c.id))) {
        return { kind: "card", card: c };
      }
    }
    return null;
  }

  private onPointerDown(e: PointerEvent): void {
    const sx = e.offsetX;
    const sy = e.offsetY;
    this.lastPointerSx = sx;
    this.lastPointerSy = sy;
    const w = this.camera.screenToWorld(sx, sy);

    if (e.button === 1 || (e.button === 0 && this.spaceDown) || (e.button === 0 && this.tool === "pan")) {
      this.mode = "pan";
      this.lastSx = sx;
      this.lastSy = sy;
      this.canvas.style.cursor = "grabbing";
      return;
    }
    if (e.button !== 0) return;

    // Text tool: click drops a new text card here, then revert to select.
    // preventDefault stops the browser moving focus to <body> after pointerdown,
    // which would blur the just-opened editor and discard the empty node.
    if (this.tool === "text") {
      if (this.editingId) return;
      e.preventDefault();
      this.addTextNodeAt(w.x, w.y);
      return;
    }

    // Comentario: un click deja el ancla y abre la burbuja para escribir.
    if (this.tool === "comment") {
      if (this.editingId) return;
      e.preventDefault();
      this.addCommentAt(w.x, w.y);
      return;
    }

    // Pencil: start a freehand stroke; points accumulate on pointer move and
    // the stroke is committed to the store (one undo step) on pointer up.
    if (this.tool === "draw") {
      e.preventDefault();
      this.mode = "draw";
      this.currentStroke = [w.x, w.y];
      this.inkDirty = true;
      return;
    }

    // Conector: arrastrar de un puerto (o, con la herramienta activa, desde
    // cualquier punto de un nodo) hasta otro nodo.
    // Con una arista seleccionada, sus extremos son manijas: arrastrarlas la
    // desengancha y la lleva a otro nodo.
    const ends = this.selectedEdgeScreenEnds();
    if (ends) {
      const which = endpointAt(ends, sx, sy);
      if (which !== null) {
        e.preventDefault();
        this.mode = "connect";
        this.connect.beginReconnect([...this.selectedEdgeIds][0], which === 0 ? "from" : "to");
        this.connect.update(w.x, w.y, sx, sy);
        return;
      }
    }

    const port = this.tool === "connector" ? this.connect.portUnderPointer(sx, sy) : null;
    if (port) {
      e.preventDefault();
      this.mode = "connect";
      this.connect.begin(port.nodeId, port.side, sx, sy);
      return;
    }
    if (this.tool === "connector") {
      e.preventDefault();
      const from = this.cardAt(w.x, w.y);
      if (from) {
        // Sin puerto explícito, el lado lo elige la geometría al arrastrar.
        this.mode = "connect";
        this.connect.begin(from.id, null, sx, sy);
      }
      return;
    }

    // Contenedor y forma se dibujan igual: arrastrando para definir su caja.
    if (this.tool === "container" || this.tool === "shape") {
      e.preventDefault();
      this.mode = this.tool === "shape" ? "draw-shape" : "draw-container";
      this.marqueeStart = { x: w.x, y: w.y };
      this.containerStartWx = w.x;
      this.containerStartWy = w.y;
      this.containerEndWx = w.x;
      this.containerEndWy = w.y;
      return;
    }

    if (this.selectedIds.size === 1) {
      const id = this.firstSelected()!;
      const dir = this.resizeDirAt(id, w.x, w.y);
      if (dir) {
        this.mode = "resize";
        this.resizeId = id;
        this.resizeDir = dir;
        const c = this.currentCard(id)!;
        this.resizeStart = { x: c.x, y: c.y, width: c.width, height: c.height };
        this.gestureSnapshotted = false;
        return;
      }
    }

    let hit = this.hitTest(w.x, w.y);

    // Una arista se selecciona y nada más: no se arrastra (moverla no significa
    // nada — su ruta sale de sus nodos) y no entra en un gesto de movimiento.
    if (hit?.kind === "edge") {
      if (e.shiftKey) {
        if (this.selectedEdgeIds.has(hit.edge.id)) this.selectedEdgeIds.delete(hit.edge.id);
        else this.selectedEdgeIds.add(hit.edge.id);
      } else {
        this.selectEdge(hit.edge.id);
      }
      this.mode = "none";
      return;
    }

    // Cmd/Ctrl sube al contenedor: sobre un hijo toma el grupo que lo tiene, y
    // sobre un hueco de adentro toma el grupo del hueco. Es el escape de la
    // regla espacial de arriba —el cuerpo del grupo ya no se agarra solo— para
    // cuando lo que se quiere mover es el contenedor y no lo que hay adentro.
    // El modificador acá es libre: el bypass del imán lo lee durante el
    // arrastre, no en el pointerdown.
    if (e.metaKey || e.ctrlKey) {
      const up = this.groupToEscalate(w.x, w.y, hit?.kind === "card" ? hit.card : null);
      if (up) hit = { kind: "card", card: up };
    }

    if (hit) {
      const card = hit.card;
      if (e.shiftKey) {
        if (this.selectedIds.has(card.id)) this.selectedIds.delete(card.id);
        else this.selectedIds.add(card.id);
      } else if (!this.selectedIds.has(card.id)) {
        // Un click selecciona, cualquiera sea el nodo; para escribir hay que
        // hacer doble click. El texto suelto era la excepción —un click abría su
        // editor— y esa excepción es la que hacía imposible arrastrarlo sin
        // caer adentro del texto.
        this.select(card.id);
      }
      if (this.selectedIds.has(card.id)) {
        this.mode = "move";
        this.moveLastWx = w.x;
        this.moveLastWy = w.y;
        this.snapDx = 0;
        this.snapDy = 0;
        this.gestureSnapshotted = false;
        this.dropGroupId = null;
        this.dropTargetId = null;
      } else {
        this.mode = "none";
      }
    } else {
      if (!e.shiftKey) this.select(null);
      this.mode = "marquee";
      this.marqueeStart = { x: w.x, y: w.y };
      this.marqueeBase = new Set(this.selectedIds);
      this.marqueeBaseEdges = new Set(this.selectedEdgeIds);
    }
  }

  private onPointerMove(e: PointerEvent): void {
    const sx = e.offsetX;
    const sy = e.offsetY;
    this.lastPointerSx = sx;
    this.lastPointerSy = sy;
    if (this.mode === "none") {
      this.commentLayer.hoverAt(sx, sy);
      this.updateHoverCursor(sx, sy);
      this.connect.hover(sx, sy, this.tool === "connector");
      return;
    }
    const w = this.camera.screenToWorld(sx, sy);

    if (this.mode === "pan") {
      this.camera.panBy((sx - this.lastSx) * PAN_SPEED, (sy - this.lastSy) * PAN_SPEED);
      this.lastSx = sx;
      this.lastSy = sy;
    } else if (this.mode === "move") {
      const dx = w.x - this.moveLastWx;
      const dy = w.y - this.moveLastWy;
      if (dx !== 0 || dy !== 0) {
        this.beginGesture();
        const move = this.snappedMove(dx, dy, e.metaKey || e.ctrlKey);
        this.store.getState().moveCards(this.selectedIds, move.dx, move.dy);
        this.moveLastWx = w.x;
        this.moveLastWy = w.y;
        this.updateDropTarget(w.x, w.y);
      }
    } else if (this.mode === "resize" && this.resizeId && this.resizeDir && this.resizeStart) {
      this.beginGesture();
      const rect = this.snappedResize(w.x, w.y, e.shiftKey, e.metaKey || e.ctrlKey);
      this.store.getState().updateCard(this.resizeId, rect);
    } else if (
      this.mode === "marquee" ||
      this.mode === "draw-container" ||
      this.mode === "draw-shape"
    ) {
      // Con Shift, una forma sale de proporción 1:1 — de ahí salen el cuadrado y
      // el círculo sin dos entradas más en el selector. El punto ya restringido
      // es el que se guarda, así que la previa que se ve mientras se arrastra y
      // lo que queda al soltar son por fuerza lo mismo.
      const end =
        this.mode === "draw-shape" && e.shiftKey
          ? squareCorner(this.containerStartWx, this.containerStartWy, w.x, w.y)
          : { x: w.x, y: w.y };
      if (this.mode !== "marquee") {
        this.containerEndWx = end.x;
        this.containerEndWy = end.y;
      }
      this.updateMarquee(end.x, end.y);
    } else if (this.mode === "connect") {
      this.connect.update(w.x, w.y, sx, sy);
    } else if (this.mode === "draw" && this.currentStroke) {
      const pts = this.currentStroke;
      const dx = w.x - pts[pts.length - 2];
      const dy = w.y - pts[pts.length - 1];
      // Distance threshold is in SCREEN px so sampling density follows zoom.
      if (Math.hypot(dx, dy) * this.camera.zoom >= INK_MIN_DIST) {
        pts.push(w.x, w.y);
        this.inkDirty = true;
      }
    }
  }

  private onPointerUp(): void {
    this.guides.clear();
    if (this.mode === "move") {
      this.commitDrop();
    } else if (this.mode === "pan") {
      this.commitCamera();
      this.canvas.style.cursor = this.spaceDown ? "grab" : this.tool === "pan" ? "grab" : "default";
    } else if (this.mode === "resize" && this.resizeId) {
      // Text cards must re-rasterize at the new size; image textures are
      // size-independent (the sprite just scales) and ink is vector (its
      // CardView repaints from the rect), so leave those caches intact.
      const card = this.currentCard(this.resizeId);
      const bitmap = card?.type === "file" && (isImagePath(card.file) || isPdfPath(card.file));
      // Estirar un texto a lo ancho es elegirle el ancho: deja de ser
      // auto-ancho y a partir de acá el texto envuelve adentro de la caja. Sin
      // esto, la primera tecla siguiente lo devolvía al tamaño de su contenido.
      if (
        card?.type === "text" &&
        card.text?.autoWidth &&
        (this.resizeDir?.includes("e") || this.resizeDir?.includes("w"))
      ) {
        this.store.getState().updateCard(this.resizeId, {
          text: { ...card.text, autoWidth: false },
          updatedAt: Date.now(),
        });
      }
      // El contenedor no cachea nada (es un recuadro vectorial) y el texto
      // tampoco (lo dibuja el DOM), así que no hay textura que invalidar.
      if (!bitmap && card?.type !== "ink" && card?.type !== "group" && card?.type !== "text") {
        this.cache.invalidate(this.resizeId);
        this.store.getState().updateCard(this.resizeId, { updatedAt: Date.now() });
      }
      this.resizeId = null;
      this.resizeDir = null;
      this.resizeStart = null;
      this.hoverResizeDir = null;
    } else if (this.mode === "marquee") {
      this.marquee.clear();
      this.marquee.visible = false;
    } else if (this.mode === "draw-container" || this.mode === "draw-shape") {
      const wasShape = this.mode === "draw-shape";
      this.marquee.clear();
      this.marquee.visible = false;
      const x0 = Math.min(this.containerStartWx, this.containerEndWx);
      const y0 = Math.min(this.containerStartWy, this.containerEndWy);
      const x1 = Math.max(this.containerStartWx, this.containerEndWx);
      const y1 = Math.max(this.containerStartWy, this.containerEndWy);
      const w = x1 - x0;
      const h = y1 - y0;
      if (w > 20 && h > 20) {
        if (wasShape) this.createShape(x0, y0, w, h);
        else this.createContainer(x0, y0, w, h);
      }
      this.setTool("select");
    } else if (this.mode === "draw") {
      if (this.currentStroke && this.currentStroke.length >= 2) {
        this.commitStroke(this.currentStroke);
      }
      this.currentStroke = null;
      this.inkDirty = true;
    } else if (this.mode === "connect") {
      this.connect.commit();
    }
    this.mode = "none";
    // El hover quedó congelado donde empezó el gesto: si no se recalcula, los
    // puertos siguen dibujados sobre la tarjeta de la que se salió y no sobre
    // la que está debajo del puntero ahora. Sólo con el conector activo, que
    // es la única herramienta que muestra puertos.
    this.connect.hover(
      this.lastPointerSx,
      this.lastPointerSy,
      this.tool === "connector",
    );
  }

  /**
   * Turn a finished pencil stroke into an `ink` card whose rect is the
   * stroke's bounding box (padded by half the stroke width so the visible ink
   * fills the box). Points are stored normalized to that rect, which is what
   * makes the card movable/resizable like an image afterwards.
   */
  private commitStroke(pts: number[]): void {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pts.length; i += 2) {
      minX = Math.min(minX, pts[i]);
      maxX = Math.max(maxX, pts[i]);
      minY = Math.min(minY, pts[i + 1]);
      maxY = Math.max(maxY, pts[i + 1]);
    }
    const pad = INK_WIDTH / 2;
    minX -= pad;
    minY -= pad;
    const width = maxX - minX + pad;
    const height = maxY - minY + pad;
    const norm = pts.map((v, i) =>
      i % 2 === 0 ? (v - minX) / width : (v - minY) / height,
    );
    const store = this.store.getState();
    store.transaction(() => {
      const card = store.addCard({
        type: "ink",
        x: minX,
        y: minY,
        width,
        height,
        markdown: "",
        ink: {
          color: this.toolbar.colorFor("draw"),
          size: INK_WIDTH,
          points: norm,
          naturalWidth: width,
          naturalHeight: height,
        },
      });
      store.adoptIntoGroups([card.id]);
    });
  }

  /** Push an undo checkpoint once, on the first real change of a drag gesture. */
  // ---- soltar dentro de un grupo -------------------------------------------

  /**
   * Grupo bajo el puntero que se quedaría con lo que se está arrastrando, o
   * null si ahí no hay ninguno.
   *
   * Se mira el puntero y no el rectángulo de la tarjeta porque durante un
   * arrastre la tarjeta suele solaparse con varios grupos a la vez: el puntero
   * es lo único que dice sin ambigüedad "acá la estoy soltando".
   *
   * Arrastrar un grupo nunca tiene destino: no anidamos grupos.
   */
  private groupUnderPointer(worldX: number, worldY: number): string | null {
    if (this.selectedIds.size === 0) return null;
    const cards = this.store.getState().doc.cards;

    for (const id of this.selectedIds) {
      if (cards.find((c) => c.id === id)?.type === "group") return null;
    }

    for (let i = cards.length - 1; i >= 0; i--) {
      const c = cards[i];
      if (c.type !== "group" || this.selectedIds.has(c.id)) continue;
      if (worldX >= c.x && worldX <= c.x + c.width && worldY >= c.y && worldY <= c.y + c.height) {
        return c.id;
      }
    }
    return null;
  }

  /** ¿Este grupo ya contiene todo lo que se arrastra? */
  private groupHasAll(groupId: string): boolean {
    const group = this.currentCard(groupId);
    const children = new Set(group?.childrenIds ?? []);
    return [...this.selectedIds].every((id) => children.has(id));
  }

  /**
   * El desplazamiento a aplicar, ya pasado por el imán de alineación.
   *
   * Se razona en posiciones y no en deltas. Si P es donde estaría lo arrastrado
   * siguiendo sólo al puntero y S la corrección del imán, lo que está en
   * pantalla es P+S. Al mover el puntero en `d`, el destino es P+d+S′, así que
   * hay que desplazar `d − S + S′`: **restar la corrección vieja antes de sumar
   * la nueva.** Sin eso el imán se acumula frame a frame y lo arrastrado se
   * despega del puntero.
   *
   * Con Cmd/Ctrl apretado el imán se apaga: es la salida de emergencia para
   * cuando lo que se quiere es justamente NO alinear con nada.
   */
  private snappedMove(dx: number, dy: number, bypass: boolean): { dx: number; dy: number } {
    // Dónde estaría todo si nunca hubiera habido imán.
    const freeDx = dx - this.snapDx;
    const freeDy = dy - this.snapDy;

    const moving: SnapRect[] = [];
    for (const c of this.store.getState().doc.cards) {
      if (!this.selectedIds.has(c.id)) continue;
      moving.push({ x: c.x + freeDx, y: c.y + freeDy, width: c.width, height: c.height });
    }

    const box = boundsOf(moving);
    const others = box ? this.snapTargets(this.selectedIds) : [];
    if (!box || bypass || others.length === 0) {
      this.guides.clear();
      this.snapDx = 0;
      this.snapDy = 0;
      return { dx: freeDx, dy: freeDy };
    }

    const snap = snapMove(box, others, SNAP_PX / this.camera.zoom);
    this.paintGuides(snap);
    this.snapDx = snap.dx;
    this.snapDy = snap.dy;
    return { dx: freeDx + snap.dx, dy: freeDy + snap.dy };
  }

  /**
   * Contra qué se alinea: todo lo VISIBLE que no se esté manipulando.
   *
   * Las dos exclusiones son las que arreglan enganches que no tenían sentido.
   * Los hijos de un contenedor arrastrado se mueven CON él (`moveCards` expande
   * la selección), así que dejarlos de candidatos hacía que el contenedor se
   * enganchara con su propio contenido y se pegoteara al arrancar. Y al revés,
   * los contenedores estaban excluidos de la lista y no había manera de alinear
   * una tarjeta con el borde de la caja que la contiene, que es el caso más
   * pedido de todos.
   *
   * Sólo se miran los rects VISIBLES: alinear con algo fuera de pantalla no se
   * puede ver, y así el costo es proporcional a lo que hay a la vista.
   */
  private snapTargets(busy: ReadonlySet<string>): SnapRect[] {
    const { doc } = this.store.getState();
    const view = this.camera.visibleWorldRect(this.app.screen.width, this.app.screen.height);

    const skip = new Set(busy);
    for (const c of doc.cards) {
      if (busy.has(c.id) && c.childrenIds) {
        for (const childId of c.childrenIds) skip.add(childId);
      }
    }

    const others: SnapRect[] = [];
    for (const c of doc.cards) {
      if (skip.has(c.id)) continue;
      if (
        c.x < view.x + view.width &&
        c.x + c.width > view.x &&
        c.y < view.y + view.height &&
        c.y + c.height > view.y
      ) {
        others.push({ x: c.x, y: c.y, width: c.width, height: c.height });
      }
    }
    return others;
  }

  /**
   * Las guías del imán: líneas de alineación y medidas de reparto.
   *
   * Las líneas antes cruzaban la pantalla de lado a lado, y con una fila de
   * diez cajas no se podía saber cuál de todas había enganchado. Ahora el tramo
   * abarca lo que se está moviendo y todos los que comparten esa línea, con un
   * margen para que se lea como una guía y no como un subrayado.
   *
   * Las medidas de reparto son otra cosa y se dibujan como tales: una barrita
   * con topes en las puntas ADENTRO del hueco, que es lo que dice "este espacio
   * y aquél miden lo mismo". Van del mismo color; lo que las distingue de una
   * guía de alineación es la forma, no el color.
   *
   * Todo esto es tinta, no color: la app no tiene colores propios (ver
   * `docs/design-system.md` §1). Las guías de alineación van PUNTEADAS, y es lo
   * único que las separa de un trazo de selección ahora que comparten tono —la
   * forma haciendo el trabajo que antes hacía el magenta, que encima era
   * exactamente uno de los seis con los que se puede dibujar.
   */
  private paintGuides(snap: SnapResult): void {
    this.guides.clear();
    const margin = 12; // px de pantalla que sobresale a cada punta
    let painted = false;

    if (snap.guideX !== null && snap.spanY) {
      const x = Math.round(this.camera.worldToScreen(snap.guideX, 0).x) + 0.5;
      const a = this.camera.worldToScreen(0, snap.spanY[0]).y - margin;
      const b = this.camera.worldToScreen(0, snap.spanY[1]).y + margin;
      this.guides.moveTo(x, a).lineTo(x, b);
      painted = true;
    }
    if (snap.guideY !== null && snap.spanX) {
      const y = Math.round(this.camera.worldToScreen(0, snap.guideY).y) + 0.5;
      const a = this.camera.worldToScreen(snap.spanX[0], 0).x - margin;
      const b = this.camera.worldToScreen(snap.spanX[1], 0).x + margin;
      this.guides.moveTo(a, y).lineTo(b, y);
      painted = true;
    }

    for (const gap of snap.gapsX) {
      const from = this.camera.worldToScreen(gap.from, gap.cross);
      const to = this.camera.worldToScreen(gap.to, gap.cross);
      const y = Math.round(from.y) + 0.5;
      const x0 = Math.round(from.x) + 0.5;
      const x1 = Math.round(to.x) + 0.5;
      // Un hueco que en pantalla no llega a un par de píxeles no se puede
      // dibujar: los dos topes se pisarían y quedaría un borrón.
      if (x1 - x0 < GAP_MIN_PX) continue;
      this.guides.moveTo(x0, y).lineTo(x1, y);
      this.guides.moveTo(x0, y - GAP_CAP_PX).lineTo(x0, y + GAP_CAP_PX);
      this.guides.moveTo(x1, y - GAP_CAP_PX).lineTo(x1, y + GAP_CAP_PX);
      painted = true;
    }
    for (const gap of snap.gapsY) {
      const from = this.camera.worldToScreen(gap.cross, gap.from);
      const to = this.camera.worldToScreen(gap.cross, gap.to);
      const x = Math.round(from.x) + 0.5;
      const y0 = Math.round(from.y) + 0.5;
      const y1 = Math.round(to.y) + 0.5;
      if (y1 - y0 < GAP_MIN_PX) continue;
      this.guides.moveTo(x, y0).lineTo(x, y1);
      this.guides.moveTo(x - GAP_CAP_PX, y0).lineTo(x + GAP_CAP_PX, y0);
      this.guides.moveTo(x - GAP_CAP_PX, y1).lineTo(x + GAP_CAP_PX, y1);
      painted = true;
    }

    if (painted) this.guides.stroke({ width: 1, color: 0xda3aa6, alpha: 0.9 });
  }

  /**
   * Recalcula el destino del arrastre. Se guardan dos cosas distintas a
   * propósito: `dropGroupId` es lo que manda al soltar, y `dropTargetId` es
   * sólo el resaltado — no se pinta nada cuando la tarjeta ya es de ese grupo,
   * porque ahí soltar no cambia nada y el azul sería ruido.
   */
  private updateDropTarget(worldX: number, worldY: number): void {
    const group = this.groupUnderPointer(worldX, worldY);
    this.dropGroupId = group;
    this.dropTargetId = group && !this.groupHasAll(group) ? group : null;
  }

  /** ¿Alguna de estas tarjetas pertenece hoy a algún grupo? */
  private anyGrouped(ids: Set<string>): boolean {
    return this.store
      .getState()
      .doc.cards.some(
        (c) => c.type === "group" && (c.childrenIds ?? []).some((childId) => ids.has(childId)),
      );
  }

  /**
   * Cierre del arrastre: la selección entra al grupo resaltado, o sale del que
   * estuviera si se soltó afuera de todo (si no, mover el grupo la teletransportaría
   * de vuelta). Corre sin checkpoint propio: se deshace junto con el movimiento.
   */
  private commitDrop(): void {
    const target = this.dropGroupId;
    this.dropGroupId = null;
    this.dropTargetId = null;
    // Sin movimiento no hubo arrastre: un click simple no debe cambiar nada.
    if (!this.gestureSnapshotted || this.selectedIds.size === 0) return;
    for (const id of this.selectedIds) {
      if (this.currentCard(id)?.type === "group") return;
    }
    if (target) {
      this.store.getState().setCardsGroup(target, this.selectedIds);
    } else if (this.anyGrouped(this.selectedIds)) {
      this.store.getState().setCardsGroup(null, this.selectedIds);
    }
  }

  private beginGesture(): void {
    if (this.gestureSnapshotted) return;
    this.gestureSnapshotted = true;
    this.store.getState().snapshot();
  }

  /**
   * Resize rect for the current pointer, anchoring the corner opposite the one
   * being dragged. When `proportional` (Shift held), the card keeps the aspect
   * ratio it had when the gesture began; the pointer drives whichever axis moved
   * more and the other is derived from the ratio.
   */
  private resizeForPointer(
    wx: number,
    wy: number,
    proportional: boolean,
  ): { x: number; y: number; width: number; height: number } {
    // Ink can shrink much further than content cards — a doodle has no
    // minimum readable size. Una forma también baja más que una tarjeta: una
    // caja de diagrama con una palabra adentro es legítima.
    const card = this.currentCard(this.resizeId!);
    const isInk = card?.type === "ink";
    const shape = card?.type === "shape" ? card.shape : undefined;
    // Un nodo de texto baja tanto como una forma: una palabra suelta es un nodo
    // legítimo, y su alto lo termina de decidir el texto unas líneas más abajo.
    const text = card?.type === "text" ? card.text : undefined;
    const MIN_W = isInk ? 12 : shape ? SHAPE_MIN_W : text ? TEXT_MIN_W : 120;
    const MIN_H = isInk ? 12 : shape ? SHAPE_MIN_H : text ? TEXT_MIN_H : 80;
    const start = this.resizeStart!;
    const dir = this.resizeDir!;
    const left = start.x;
    const top = start.y;
    const right = start.x + start.width;
    const bottom = start.y + start.height;

    // Raw drag extents from the anchored (opposite) edge.
    let width = dir.includes("w") ? right - wx : dir.includes("e") ? wx - left : start.width;
    let height = dir.includes("n") ? bottom - wy : dir.includes("s") ? wy - top : start.height;
    width = Math.max(MIN_W, width);
    height = Math.max(MIN_H, height);

    if (proportional) {
      // Escalar los dos lados del rect original por un mismo factor conserva la
      // proporción. Manda el eje que el gesto de verdad arrastra: tomar siempre
      // el mayor de los dos dejaba a un lado (`e`, `w`, `n`, `s`) sin poder
      // achicar nada, porque el eje quieto aportaba un factor 1 que ganaba.
      const horiz = dir.includes("e") || dir.includes("w");
      const vert = dir.includes("n") || dir.includes("s");
      let scale =
        horiz && vert
          ? Math.max(width / start.width, height / start.height)
          : horiz
            ? width / start.width
            : height / start.height;
      scale = Math.max(scale, MIN_W / start.width, MIN_H / start.height);
      width = start.width * scale;
      height = start.height * scale;
    }

    // El ancho es de quien redimensiona; el alto no baja de lo que el texto
    // necesita para entrar (plan §D7.2). Va DESPUÉS de todo lo demás y antes de
    // calcular x/y, para que arrastrar desde el norte o el oeste siga anclando
    // bien la esquina opuesta.
    if (shape) height = Math.max(height, fitHeight(shape.kind, width, shape.text));
    // El texto suelto va más lejos que la forma: su alto ES el del texto, ni más
    // ni menos. Redimensionarlo es elegirle el ancho; el alto no es negociable
    // porque no hay nada más adentro de la caja.
    if (text) height = textNodeSize({ ...text, autoWidth: false }, width).height;

    // El borde opuesto al arrastrado queda clavado. En un gesto de un solo eje
    // con proporción, el otro eje crece a los dos lados: si se anclara arriba,
    // estirar por el este haría bajar el nodo, que no es lo que se pidió.
    const x = dir.includes("w")
      ? right - width
      : dir.includes("e")
        ? left
        : left + (start.width - width) / 2;
    const y = dir.includes("n")
      ? bottom - height
      : dir.includes("s")
        ? top
        : top + (start.height - height) / 2;
    return { x, y, width, height };
  }

  /**
   * El rect del gesto de resize en curso, ya pasado por el imán.
   *
   * Redimensionar no tenía imán ninguno: se podía alinear moviendo, pero al
   * estirar un nodo hasta el borde de otro había que acertar el píxel. Acá sólo
   * enganchan los bordes que el puntero arrastra (ver `snapResize`), y la
   * corrección se le suma al PUNTERO —no al rect— para que los mínimos de
   * tamaño y el alto que exige el texto de una forma sigan mandando después.
   */
  private snappedResize(
    wx: number,
    wy: number,
    proportional: boolean,
    bypass: boolean,
  ): { x: number; y: number; width: number; height: number } {
    const raw = this.resizeForPointer(wx, wy, proportional);
    // Con proporción el segundo eje sale del primero, así que engancharlo
    // rompería la proporción justo cuando se pidió conservarla.
    if (bypass || proportional) {
      this.guides.clear();
      return raw;
    }

    const others = this.snapTargets(new Set([this.resizeId!]));
    if (others.length === 0) {
      this.guides.clear();
      return raw;
    }

    const snap = snapResize(raw, this.resizeDir!, others, SNAP_PX / this.camera.zoom);
    const rect = this.resizeForPointer(wx + snap.dx, wy + snap.dy, proportional);
    // La guía se dibuja sobre el rect FINAL: si un mínimo de tamaño frenó el
    // borde, el imán no enganchó y no hay nada que anunciar.
    const eps = 1e-6;
    const hitX =
      snap.guideX !== null &&
      (Math.abs(rect.x - snap.guideX) < eps || Math.abs(rect.x + rect.width - snap.guideX) < eps);
    const hitY =
      snap.guideY !== null &&
      (Math.abs(rect.y - snap.guideY) < eps || Math.abs(rect.y + rect.height - snap.guideY) < eps);
    this.paintGuides({
      dx: 0,
      dy: 0,
      guideX: hitX ? snap.guideX : null,
      guideY: hitY ? snap.guideY : null,
      spanY: hitX ? this.unionSpan(snap.spanY, rect.y, rect.y + rect.height) : null,
      spanX: hitY ? this.unionSpan(snap.spanX, rect.x, rect.x + rect.width) : null,
      gapsX: [],
      gapsY: [],
    });
    return rect;
  }

  private unionSpan(span: [number, number] | null, a: number, b: number): [number, number] {
    if (!span) return [a, b];
    return [Math.min(span[0], a), Math.max(span[1], b)];
  }

  /**
   * Zona de resize bajo un punto, para el nodo seleccionado.
   *
   * Tarjetas, formas y contenedores pasan por la MISMA función: las ocho
   * direcciones valen para los tres, y lo único que cambia es el ancho de la
   * banda agarrable. No se consulta la vista porque un nodo culleado (fuera de
   * pantalla) no tiene vista, y el rect del modelo alcanza y sobra.
   */
  private resizeDirAt(id: string, wx: number, wy: number): ResizeDir | null {
    const card = this.currentCard(id);
    if (!card) return null;
    if (card.type === "comment") return null;
    if (card.type !== "group") return resizeDirOn(card, wx, wy, this.camera.zoom);
    // La banda del contenedor entra 9 px hacia adentro (20 en las esquinas) y
    // se consulta ANTES que el hit-test: sin esta guarda le comía el click a
    // cualquier hijo pegado al borde. Con ella gana el hijo, y el contenedor se
    // sigue redimensionando desde la parte de la banda que sobresale hacia
    // afuera, que es donde casi nunca hay nada.
    if (this.childAt(wx, wy)) return null;
    return resizeDirOn(card, wx, wy, this.camera.zoom, GROUP_HIT_BAND);
  }

  /**
   * ¿El punto cae en la banda del borde de este contenedor?
   *
   * Sale de la MISMA geometría que decide el resize y eso no es casualidad: la
   * zona que dice "esto es el grupo" y la que lo redimensiona tienen que ser el
   * mismo píxel. Va por `resizeDirOn` y no por `resizeDirAt` a propósito: acá
   * se pregunta por el borde, no por quién gana el gesto.
   */
  private onGroupBand(card: Card, wx: number, wy: number): boolean {
    return resizeDirOn(card, wx, wy, this.camera.zoom, GROUP_HIT_BAND) !== null;
  }

  /**
   * El cursor de resize sobre la zona agarrable, y el agarre que se pinta lleno
   * mientras el puntero está encima. Las dos cosas salen de la misma consulta:
   * lo que el cursor promete es exactamente lo que el click va a tomar.
   */
  private updateHoverCursor(sx: number, sy: number): void {
    if (this.spaceDown || this.tool !== "select") {
      this.hoverResizeDir = null; // otras herramientas mandan sobre el cursor
      return;
    }
    let dir: ResizeDir | null = null;
    if (this.selectedIds.size === 1) {
      const w = this.camera.screenToWorld(sx, sy);
      dir = this.resizeDirAt(this.firstSelected()!, w.x, w.y);
    }
    this.hoverResizeDir = dir;
    const cursor = resizeCursor(dir);
    if (this.canvas.style.cursor !== cursor) this.canvas.style.cursor = cursor;
  }

  /** El cromo de selección que le toca a un nodo en este frame. */
  private chromeFor(id: string): SelectionChrome {
    // Con varios seleccionados no hay gesto de resize, así que tampoco agarres:
    // ocho por nodo serían ruido que promete algo que no se puede hacer.
    if (this.selectedIds.size !== 1 || !this.selectedIds.has(id)) return {};
    return {
      handles: true,
      // En pleno gesto manda la dirección que se está arrastrando; fuera de
      // todo gesto, la que el puntero está señalando. Durante un arrastre que
      // no es de resize (mover, marquesina) no se resalta nada: el `hover`
      // guardado quedó viejo en cuanto empezó el gesto.
      hover:
        this.mode === "resize"
          ? this.resizeDir
          : this.mode === "none"
            ? this.hoverResizeDir
            : null,
    };
  }

  /**
   * Recuadro de arrastre. La marquesina de selección va en el azul de siempre;
   * el contenedor y la forma en curso se previsualizan con el color activo de
   * la paleta, para que lo que ves mientras arrastrás sea lo que queda al
   * soltar. La forma va más lejos: se dibuja con su silueta y su relleno de
   * verdad, no con un rectángulo tenue que después se convierte en otra cosa.
   */
  private updateMarquee(wx: number, wy: number): void {
    const x0 = Math.min(this.marqueeStart.x, wx);
    const y0 = Math.min(this.marqueeStart.y, wy);
    const x1 = Math.max(this.marqueeStart.x, wx);
    const y1 = Math.max(this.marqueeStart.y, wy);

    // Dibujar una forma no toca la selección: el recuadro es sólo la previa de
    // lo que se va a crear. El contenedor sí la usa, para saber a quién envuelve.
    if (this.mode !== "draw-shape") {
      const { doc } = this.store.getState();
      const next = new Set(this.marqueeBase);
      for (const c of doc.cards) {
        // Un contenedor entra sólo si el recuadro lo abarca ENTERO; el resto,
        // con tocarlo alcanza. Con la regla de intersección para todos, un lazo
        // adentro del grupo rozaba su rect y se lo llevaba puesto: no había
        // forma de seleccionar dos tarjetas de adentro sin agarrar el grupo.
        const inside =
          c.type === "group"
            ? c.x >= x0 && c.x + c.width <= x1 && c.y >= y0 && c.y + c.height <= y1
            : c.x < x1 && c.x + c.width > x0 && c.y < y1 && c.y + c.height > y0;
        if (inside) next.add(c.id);
      }
      this.selectedIds = next;

      // Una arista entra si entran SUS DOS extremos. Encerrar sólo la línea no
      // la selecciona: sin sus nodos no hay nada que hacer con ella.
      if (this.mode === "marquee") {
        const nextEdges = new Set(this.marqueeBaseEdges);
        for (const e of doc.edges) {
          if (next.has(e.fromNode) && next.has(e.toNode)) nextEdges.add(e.id);
        }
        this.selectedEdgeIds = nextEdges;
      }
    }

    const a = this.camera.worldToScreen(x0, y0);
    const b = this.camera.worldToScreen(x1, y1);
    const previewColor =
      this.mode === "draw-shape"
        ? this.toolbar.colorFor("shape")
        : this.mode === "draw-container"
          ? this.toolbar.colorFor("container")
          : this.toolbar.colorFor("draw");
    const paint = this.mode === "marquee" ? UI_SELECTION : inkColor(previewColor);
    this.marquee.clear();

    if (this.mode === "draw-shape") {
      // La previa de una forma es la forma: su silueta real y su relleno real,
      // con la misma función que la dibuja después. Un rombo se ve rombo
      // mientras se arrastra, y con Shift se ve que está saliendo un círculo.
      // Queda apenas translúcida para no perder de vista lo que hay debajo.
      paintShapePath(this.marquee, a.x, a.y, b.x - a.x, b.y - a.y, this.toolbar.shape);
      this.marquee.fill({ color: paint, alpha: 0.85 });
    } else {
      this.marquee
        .rect(a.x, a.y, b.x - a.x, b.y - a.y)
        .fill({ color: paint, alpha: 0.12 })
        .stroke({ width: 1, color: paint, alpha: 0.9 });
    }
    this.marquee.visible = true;
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      let delta = e.deltaY;
      if (e.deltaMode === 1) delta *= 16;
      else if (e.deltaMode === 2) delta *= this.app.screen.height;
      delta = Math.max(-80, Math.min(80, delta));
      const speed = e.ctrlKey ? ZOOM_SPEED_PINCH : ZOOM_SPEED_WHEEL;
      const factor = Math.exp(-delta * speed);
      this.camera.zoomAt(factor, e.offsetX, e.offsetY);
      // Zoom in/out manual: el preferido del menú deja de valer.
      this.clearPreferredZoom();
      this.closeZoomMenu();
    } else {
      let dx = e.deltaX;
      let dy = e.deltaY;
      if (e.deltaMode === 1) {
        dx *= 16;
        dy *= 16;
      } else if (e.deltaMode === 2) {
        dx *= this.app.screen.width;
        dy *= this.app.screen.height;
      }
      this.camera.panBy(-dx * PAN_SPEED, -dy * PAN_SPEED);
    }
    this.scheduleCameraCommit();
  }

  private onDoubleClick(e: MouseEvent): void {
    const w = this.camera.screenToWorld(e.offsetX, e.offsetY);
    // Doble click sobre el nombre de un grupo = renombrarlo (igual que Figma).
    const labelled = this.groupLabelAt(w.x, w.y);
    if (labelled) {
      this.groupRename.start(labelled.id, labelled.name ?? "");
      return;
    }
    const onEdge = this.hitTest(w.x, w.y);
    if (onEdge?.kind === "edge") {
      this.selectEdge(onEdge.edge.id);
      this.startSelectedEdgeLabelEdit();
      return;
    }
    const hit = this.cardAt(w.x, w.y);
    if (!hit) return;
    // Doble click sobre un documento embebido (.md, .pdf) lo abre AL COSTADO:
    // el lienzo sigue vivo a la izquierda, con su cámara y su selección, así
    // que consultar una nota deja de costar el contexto entero. Para irse de
    // verdad está "Full view" en la tarjeta. Un `.canvas` es un lugar y no se
    // consulta al costado: el workspace lo desvía solo al área grande.
    if (hit.type === "file" && hit.file && !isImagePath(hit.file)) {
      if (this.options.onOpenFile) {
        this.keepCardVisible(hit);
        this.options.onOpenFile(hit.file, { peek: true });
      } else this.select(hit.id);
      return;
    }
    if (hit.type === "ink" || hit.type === "group") {
      this.select(hit.id);
    } else if (hit.type === "shape") {
      this.startShapeEdit(hit);
    } else if (hit.type === "text") {
      this.startTextEdit(hit);
    } else if (hit.type === "comment") {
      this.startCommentEdit(hit);
    } else {
      this.select(hit.id);
    }
  }

  /**
   * Crea un nodo de texto con su esquina superior izquierda en el punto del
   * click y abre su editor.
   *
   * Nace auto-ancho: la caja es del tamaño del texto y crece con él, como en
   * Figma cuando se hace click con la herramienta de texto (arrastrar para fijar
   * un ancho no existe todavía). La alineación, el fondo y el color son los
   * últimos elegidos en la barra.
   */
  private addTextNodeAt(wx: number, wy: number): void {
    const text = {
      text: "",
      align: this.toolbar.align,
      background: this.toolbar.textBackground,
      color: this.toolbar.textColor,
      autoWidth: true,
    };
    const { width, height } = textNodeSize(text, TEXT_MIN_W);
    const store = this.store.getState();
    let card!: Card;
    store.transaction(() => {
      card = store.addCard({ type: "text", x: wx, y: wy, width, height, markdown: "", text });
      store.adoptIntoGroups([card.id]);
    });
    this.select(card.id);
    this.startTextEdit(card, { isNewNode: true, selectToolOnClose: true });
  }

  /** Crea un ancla circular centrada en el click y abre su mensaje. */
  private addCommentAt(wx: number, wy: number): void {
    const store = this.store.getState();
    let card!: Card;
    store.transaction(() => {
      card = store.addCard({
        type: "comment",
        x: wx - 16,
        y: wy - 16,
        width: 32,
        height: 32,
        markdown: "",
        comment: {
          text: "",
          pinned: this.toolbar.commentPinned,
          bodyAutoScale: this.toolbar.commentBodyAutoScale,
        },
      });
      store.adoptIntoGroups([card.id]);
    });
    this.select(card.id);
    this.startCommentEdit(card, { isNewNode: true, selectToolOnClose: true });
  }

  /** Edición inline de la burbuja; Escape o Cmd/Ctrl+Enter confirman. */
  private startCommentEdit(
    card: Card,
    options: { isNewNode?: boolean; selectToolOnClose?: boolean } = {},
  ): void {
    this.select(card.id);
    let snapshotted = options.isNewNode === true;
    this.commentLayer.startEdit(card.id, card.comment?.text ?? "", {
      onChange: (id, text) => {
        const target = this.currentCard(id);
        if (target?.type !== "comment" || text === target.comment?.text) return;
        if (!snapshotted) {
          this.store.getState().snapshot();
          snapshotted = true;
        }
        this.store.getState().updateCard(id, {
          comment: { ...target.comment!, text },
          updatedAt: Date.now(),
        });
      },
      onClose: (id) => {
        const target = this.currentCard(id);
        if (
          options.isNewNode &&
          target?.type === "comment" &&
          (target.comment?.text ?? "").trim() === ""
        ) {
          this.store.getState().discardCards(new Set([id]));
          this.selectedIds.delete(id);
        }
        if (options.selectToolOnClose) this.setTool("select");
      },
    });
  }

  /**
   * Abre el editor de un nodo de texto.
   *
   * Es el mismo editor que el de una forma —el mismo textarea sobre la misma
   * caja— y por eso son dos métodos casi iguales: lo único que cambia es qué
   * campo del modelo se escribe y qué tamaño le corresponde a la caja después.
   *
   * Lo propio del texto suelto es el final: si el nodo se acaba de crear y se
   * cierra vacío, se descarta — es el "hice click con T y me arrepentí", y sin
   * eso quedaría basura invisible en el lienzo (es lo que hace Figma).
   *
   * Un nodo que YA existía, en cambio, no se borra aunque lo dejes vacío. Es a
   * propósito y es asimétrico: borrarlo solo convierte un "seleccioné todo y
   * apreté una tecla de más" en una pérdida de contenido que ningún ⌘Z evidente
   * deshace, mientras que una caja vacía de más se borra con Delete.
   */
  private startTextEdit(
    card: Card,
    options: { isNewNode?: boolean; selectToolOnClose?: boolean } = {},
  ): void {
    this.select(card.id);
    let snapshotted = options.isNewNode === true;
    this.textEditor.start(card.id, card.text?.text ?? "", {
      onChange: (id, value) => {
        const target = this.currentCard(id);
        if (target?.type !== "text" || value === target.text?.text) return;
        if (!snapshotted) {
          this.store.getState().snapshot();
          snapshotted = true;
        }
        const text = { ...target.text!, text: value };
        this.store.getState().updateCard(id, {
          text,
          ...textNodeSize(text, target.width),
          updatedAt: Date.now(),
        });
      },
      onClose: (id) => {
        const target = this.currentCard(id);
        if (
          options.isNewNode &&
          target?.type === "text" &&
          (target.text?.text ?? "").trim() === ""
        ) {
          this.store.getState().discardCards(new Set([id]));
          this.selectedIds.delete(id);
        }
        if (options.selectToolOnClose) this.setTool("select");
      },
    });
  }

  /**
   * Abre el editor propio de una forma.
   *
   * Es el gemelo de `startTextEdit` para el otro texto del canvas: mismo editor
   * y misma mecánica, pero la forma escribe `shape.text`, sólo CRECE de alto
   * (achicarla sola pisaría un alto elegido a mano) y no se borra al quedar
   * vacía — una caja sin rótulo en un diagrama es legítima.
   *
   * Cada tecla escribe al store, así la caja crece mientras se escribe. El
   * checkpoint de deshacer se toma en el primer cambio real: un nodo recién
   * creado ya tomó el suyo al crearse, así que toda la edición entra en el
   * mismo `⌘Z`.
   */
  private startShapeEdit(
    card: Card,
    options: { isNewNode?: boolean; selectToolOnClose?: boolean } = {},
  ): void {
    this.select(card.id);
    let snapshotted = options.isNewNode === true;
    this.textEditor.start(card.id, card.shape?.text ?? "", {
      onChange: (id, text) => {
        const target = this.currentCard(id);
        if (target?.type !== "shape" || text === target.shape?.text) return;
        if (!snapshotted) {
          this.store.getState().snapshot();
          snapshotted = true;
        }
        const shape = { ...target.shape!, text };
        this.store.getState().updateCard(id, {
          shape,
          // La caja SÓLO crece: achicarla sola pisaría el alto que alguien
          // eligió a mano, y eso no se puede deshacer con un arrastre.
          height: Math.max(target.height, fitHeight(shape.kind, target.width, text)),
          updatedAt: Date.now(),
        });
      },
      onClose: () => {
        if (options.selectToolOnClose) this.setTool("select");
      },
    });
  }

  /**
   * Le devuelve a un nodo de texto viejo el alto que su texto pide.
   *
   * Un nodo guardado antes de esta versión trae un tamaño medido con otra
   * tipografía y sin aire alrededor: al abrirlo, el mismo texto puede necesitar
   * una línea más de las que su caja tiene, y lo que sobra quedaría recortado.
   * Se corrige al aparecer en pantalla, una sola vez por nodo — mismo patrón
   * que `ensureFileLoaded`, que es el otro arreglo perezoso de este bucle.
   *
   * Sólo CRECE: el ancho y el alto que alguien eligió a mano son suyos.
   */
  private ensureTextFits(card: Card): void {
    if (!card.text || this.textFitted.has(card.id)) return;
    this.textFitted.add(card.id);
    const needed = textNodeSize({ ...card.text, autoWidth: false }, card.width).height;
    if (needed <= card.height) return;
    this.store.getState().updateCard(card.id, { height: needed });
  }

  private cardScreenRect(card: Card) {
    const tl = this.camera.worldToScreen(card.x, card.y);
    return { x: tl.x, y: tl.y, width: card.width, height: card.height, scale: this.camera.zoom };
  }

  /**
   * ¿Hay algún texto abierto para escribir? Mientras lo haya no se dibujan
   * puertos de conexión, no aparece la barra de lectura y la herramienta de
   * texto no crea un nodo nuevo encima.
   *
   * Es UN solo editor para las formas y para el texto suelto: mientras fueron
   * dos, todo lo que preguntaba por uno tenía que acordarse de preguntar por el
   * otro.
   */
  private get editingId(): string | null {
    return this.textEditor.editingId ?? this.commentLayer.editingId;
  }

  private select(id: string | null): void {
    this.selectedIds.clear();
    this.selectedEdgeIds.clear();
    if (id) this.selectedIds.add(id);
  }

  /** Selecciona una sola arista, soltando cualquier otra selección. */
  private selectEdge(id: string | null): void {
    this.selectedIds.clear();
    this.selectedEdgeIds.clear();
    if (id) this.selectedEdgeIds.add(id);
  }

  private firstSelected(): string | null {
    for (const id of this.selectedIds) return id;
    return null;
  }

  private currentCard(id: string): Card | undefined {
    return this.store.getState().doc.cards.find((c) => c.id === id);
  }

  // ---- camera persistence (debounced) ------------------------------------

  /** Tarjeta que no hay que perder de vista en el próximo achique. */
  private keepVisible: { x: number; y: number; width: number; height: number } | null = null;
  private keepVisibleTimer: number | null = null;

  /**
   * "No me pierdas esta tarjeta": el área de dibujo está por achicarse —se abre
   * el panel lateral— y lo que se acaba de tocar tiene que seguir a la vista
   * cuando eso pase.
   *
   * Se anota y se espera al `ResizeObserver` en vez de panear acá: el achique
   * lo hace el CSS un instante después y en este momento todavía no se sabe
   * cuánto va a medir el área que queda.
   *
   * El pedido VENCE solo. Si el panel ya estaba abierto no llega ningún resize,
   * y sin vencimiento el pedido quedaría esperando a uno cualquiera —colapsar
   * el rail, achicar la ventana— para paltear la vista mucho después, sin que
   * nada lo hubiera pedido.
   */
  private keepCardVisible(rect: { x: number; y: number; width: number; height: number }): void {
    this.keepVisible = { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
    if (this.keepVisibleTimer !== null) clearTimeout(this.keepVisibleTimer);
    this.keepVisibleTimer = window.setTimeout(() => {
      this.keepVisible = null;
      this.keepVisibleTimer = null;
    }, 400);
  }

  /** Paneá lo MÍNIMO para que la tarjeta anotada entre en el área que quedó. */
  private settleKeepVisible(): void {
    const rect = this.keepVisible;
    if (!rect) return;
    this.keepVisible = null;
    if (this.keepVisibleTimer !== null) {
      clearTimeout(this.keepVisibleTimer);
      this.keepVisibleTimer = null;
    }

    const margin = 24;
    const w = this.app.screen.width;
    const h = this.app.screen.height;
    const tl = this.camera.worldToScreen(rect.x, rect.y);
    const br = this.camera.worldToScreen(rect.x + rect.width, rect.y + rect.height);
    // Primero se corrige el borde que se fue de la pantalla y después el
    // opuesto: con una tarjeta más grande que el área, gana que se vea dónde
    // empieza.
    let dx = 0;
    let dy = 0;
    if (br.x > w - margin) dx = w - margin - br.x;
    if (tl.x + dx < margin) dx = margin - tl.x;
    if (br.y > h - margin) dy = h - margin - br.y;
    if (tl.y + dy < margin) dy = margin - tl.y;
    if (dx === 0 && dy === 0) return;
    this.camera.panBy(dx, dy);
    // La cámara se guarda por documento: un paneo que no se asienta se pierde
    // al cerrar, y volver al lienzo lo dejaría donde estaba antes del panel.
    this.commitCamera();
  }

  private camTimer: number | null = null;
  private scheduleCameraCommit(): void {
    if (this.camTimer !== null) clearTimeout(this.camTimer);
    this.camTimer = window.setTimeout(() => this.commitCamera(), 250);
  }

  private commitCamera(): void {
    if (this.camTimer !== null) {
      clearTimeout(this.camTimer);
      this.camTimer = null;
    }
    this.store.getState().setCamera({ x: this.camera.x, y: this.camera.y, zoom: this.camera.zoom });
  }

  // ---- per-frame loop -----------------------------------------------------

  private frame = (): void => {
    this.syncViews();
    if (this.inkDirty) {
      this.inkDirty = false;
      this.renderLiveStroke();
    }
    this.syncCardActions();
    this.syncEdgeActions();
    // Los puertos sólo con la herramienta conector: en `select` el hover queda
    // limpio. Las manijas de la arista seleccionada sí se muestran siempre en
    // reposo, para poder re-engancharla sin cambiar de herramienta.
    const repo = this.mode === "none" && !this.editingId;
    this.connect.sync(
      this.mode === "connect",
      repo && this.tool === "connector",
      repo ? this.selectedEdgeScreenEnds() : null,
    );
    if (this.hud) {
      const { doc } = this.store.getState();
      this.hud.update({
        cardsVisible: this.visibleCards,
        cardsTotal: doc.cards.length,
        edgesTotal: doc.edges.length,
        edgesRepainted: this.edgeLayer.repainted,
        zoom: this.camera.zoom,
        raster: this.cache.rasterCount,
      });
    }
    this.sleepIfIdle();
  };

  /**
   * Muestra las acciones de lectura sobre la tarjeta seleccionada, si es una
   * que embebe un documento legible.
   *
   * Se recalcula por frame (y no al cambiar la selección) porque la barra sigue
   * a la tarjeta: al mover, al hacer zoom y al desplazarse el canvas tiene que
   * quedar pegada arriba de ella. Las escrituras al DOM sólo ocurren cuando la
   * posición cambió de verdad.
   */
  private syncCardActions(): void {
    if (this.selectedEdgeIds.size > 0) {
      this.cardActions.hide();
      return;
    }
    const id = this.selectedIds.size === 1 ? this.firstSelected() : null;
    const card = id ? this.currentCard(id) : undefined;
    const path = card?.type === "file" ? card.file : undefined;
    // Las imágenes no se leen: su tarjeta ya muestra todo lo que hay.
    const readable = !!path && !isImagePath(path);
    // Durante un gesto (mover, redimensionar, marquesina, dibujo) la barra
    // estorbaría, y mientras se edita un texto la selección no es de lectura.
    if (!readable || this.mode !== "none" || this.editingId || this.tool !== "select") {
      this.cardActions.hide();
      return;
    }
    this.cardActions.show(path!, this.cardScreenRect(card!));
  }

  private syncViews(): void {
    const { doc } = this.store.getState();
    this.syncColorPalette();
    const zoom = this.camera.zoom;
    // El zoom se muestra como porcentaje entero; sólo se escribe al DOM cuando
    // cambia, porque syncViews corre por frame.
    const pct = Math.round(zoom * 100);
    if (pct !== this.lastZoomPct) {
      this.lastZoomPct = pct;
      this.navZoom.textContent = `${pct}%`;
    }
    const rect = this.camera.visibleWorldRect(this.app.screen.width, this.app.screen.height);
    const margin = Math.max(rect.width, rect.height) * 0.2;
    const vx0 = rect.x - margin;
    const vy0 = rect.y - margin;
    const vx1 = rect.x + rect.width + margin;
    const vy1 = rect.y + rect.height + margin;

    const live = new Set<string>();
    const labels: GroupLabelItem[] = [];
    const textItems: CanvasTextItem[] = [];
    const commentItems: CommentItem[] = [];
    // Para que el texto (DOM, encima de todo el canvas) respete el apilado:
    // el lugar en el documento de cada texto, y lo opaco que podría taparlo.
    const textZ: number[] = [];
    const covers: { z: number; cover: TextCover }[] = [];
    let z = -1;
    let visible = 0;
    for (const card of doc.cards) {
      z++;
      live.add(card.id);
      const isGroup = card.type === "group";
      if (isGroup) {
        let gv = this.groupViews.get(card.id);
        if (!gv) {
          gv = new GroupView();
          this.groupViews.set(card.id, gv);
        }
        if (gv.container.parent !== this.groupsLayer) {
          this.groupsLayer.addChild(gv.container);
        }
        // Fuera de pantalla no se dibuja ni se actualiza. Se oculta y no se
        // saca de la capa: los contenedores no pasan por `syncStacking`, así
        // que re-anexarlo al volver lo subiría por encima de los otros.
        const groupOnScreen =
          card.x < vx1 && card.x + card.width > vx0 && card.y < vy1 && card.y + card.height > vy0;
        gv.container.visible = groupOnScreen;
        if (!groupOnScreen) continue;
        const groupColor = inkColor(card.color ?? this.toolbar.colorFor("container"));
        const groupSelected = this.selectedIds.has(card.id);
        const isDropTarget = this.dropTargetId === card.id;
        gv.update(
          card.x,
          card.y,
          card.width,
          card.height,
          zoom,
          groupSelected,
          groupColor,
          isDropTarget,
          this.chromeFor(card.id),
        );
        labels.push({
          id: card.id,
          x: card.x,
          y: card.y,
          name: card.name ?? "",
          color: groupColor,
          selected: groupSelected,
          dropTarget: isDropTarget,
          autoScale: card.titleAutoScale === true,
          hidden: this.groupRename.editingId === card.id,
        });
        continue;
      }

      if (card.type === "comment" && card.comment) {
        commentItems.push({
          id: card.id,
          x: card.x,
          y: card.y,
          text: card.comment.text,
          pinned: card.comment.pinned,
          bodyAutoScale: card.comment.bodyAutoScale,
          selected: this.selectedIds.has(card.id),
        });
        visible++;
        continue;
      }

      const onScreen =
        card.x < vx1 && card.x + card.width > vx0 && card.y < vy1 && card.y + card.height > vy0;

      if (card.type === "shape") {
        let sv = this.shapeViews.get(card.id);
        const editing = this.textEditor.editingId === card.id;
        // Una forma que se está editando no se descarta aunque salga de
        // pantalla: si se descartara, su editor quedaría flotando en el último
        // lugar donde estuvo (con la rueda se puede desplazar el canvas sin
        // sacarle el foco al textarea).
        if (onScreen || editing) {
          if (!sv) {
            sv = new ShapeView(card);
            this.shapeViews.set(card.id, sv);
          }
          if (sv.container.parent !== this.world) this.world.addChild(sv.container);
          visible++;
          const shapeSelected = this.selectedIds.has(card.id);
          sv.update(card, zoom, shapeSelected, this.chromeFor(card.id));
          this.stackChrome(sv.selection, shapeSelected);
          const kind = card.shape?.kind ?? "rect";
          covers.push({ z, cover: shapeCover(card, kind) });
          const item: CanvasTextItem = {
            id: card.id,
            x: card.x,
            y: card.y,
            width: card.width,
            height: card.height,
            // La caja inscrita en la geometría, y el color por contraste contra
            // el relleno: lo que hace que el texto de un rombo no se escape por
            // las puntas ni quede ilegible sobre un relleno oscuro.
            box: textBox(kind, card.width, card.height),
            color: textColorFor(card.shape?.fill ?? DEFAULT_SHAPE_FILL),
            align: card.shape?.align ?? "center",
            valign: "center",
            text: card.shape?.text ?? "",
            // Mientras se edita manda el editor. Ojo: se oculta el TEXTO, no la
            // forma — que se ocultaran juntos era el bug de la figura invisible.
            hidden: editing,
          };
          textItems.push(item);
          textZ.push(z);
          // El editor se posiciona con EL MISMO ítem con el que se habría
          // dibujado la etiqueta: por eso ocupa su lugar exacto.
          if (editing) {
            this.textEditor.sync(item, this.camera.worldToScreen(card.x, card.y), zoom);
          }
        } else if (sv && sv.container.parent === this.world) {
          this.stackChrome(sv.selection, false);
          this.world.removeChild(sv.container);
        }
        continue;
      }

      this.ensureFileLoaded(card);
      let view = this.views.get(card.id);
      // Un nodo que se está editando no se descarta aunque salga de pantalla:
      // su editor quedaría flotando donde estuvo (con la rueda se desplaza el
      // canvas sin sacarle el foco al textarea). Igual que con las formas.
      const editing = this.textEditor.editingId === card.id;
      if (onScreen || editing) {
        if (!view) {
          view = new CardView(card);
          this.views.set(card.id, view);
        }
        if (view.container.parent !== this.world) this.world.addChild(view.container);
        visible++;
        const cardSelected = this.selectedIds.has(card.id);
        view.setSelected(cardSelected, this.chromeFor(card.id));
        view.update(card, zoom, this.dpr, this.cache);
        this.stackChrome(view.selection, cardSelected);
        // Lo que pinta un rect opaco tapa el texto de lo que tenga debajo; la
        // tinta no (es un trazo fino) y un texto sin fondo tampoco.
        if (card.type === "text" ? hasTextBackground(card.text) : card.type !== "ink") {
          covers.push({
            z,
            cover: {
              kind: "rect",
              x: card.x,
              y: card.y,
              width: card.width,
              height: card.height,
              radius: card.type === "text" ? TEXT_RADIUS : 4,
            },
          });
        }
        // El texto de un nodo `text` lo dibuja la misma capa DOM que el de una
        // forma; su CardView pone sólo el fondo (que puede no existir) y el
        // cromo de selección.
        if (card.type === "text" && card.text) {
          this.ensureTextFits(card);
          const item: CanvasTextItem = {
            id: card.id,
            x: card.x,
            y: card.y,
            width: card.width,
            height: card.height,
            box: textNodeBox(card.width, card.height),
            color: card.text.color,
            align: card.text.align,
            // Arriba y no centrado: un texto suelto crece hacia abajo desde
            // donde se hizo click, como en cualquier editor.
            valign: "top",
            text: card.text.text,
            hidden: editing,
          };
          textItems.push(item);
          textZ.push(z);
          if (editing) {
            this.textEditor.sync(item, this.camera.worldToScreen(card.x, card.y), zoom);
          }
        }
      } else if (view && view.container.parent === this.world) {
        this.stackChrome(view.selection, false);
        this.world.removeChild(view.container);
      }
    }

    // A cada texto, lo opaco que está más arriba en el documento y lo pisa.
    for (let k = 0; k < textItems.length; k++) {
      const item = textItems[k];
      for (const { z: cz, cover } of covers) {
        if (cz <= textZ[k]) continue;
        if (
          cover.x < item.x + item.width &&
          item.x < cover.x + cover.width &&
          cover.y < item.y + item.height &&
          item.y < cover.y + cover.height
        ) {
          (item.covers ??= []).push(cover);
        }
      }
    }

    // Clean up destroyed views
    for (const [id, view] of this.views) {
      if (!live.has(id)) {
        this.stackChrome(view.selection, false);
        if (view.container.parent === this.world) this.world.removeChild(view.container);
        view.destroy();
        this.views.delete(id);
        this.cache.invalidate(id);
      }
    }
    for (const [id, gv] of this.groupViews) {
      if (!live.has(id)) {
        gv.destroy();
        this.groupViews.delete(id);
      }
    }
    for (const [id, sv] of this.shapeViews) {
      if (!live.has(id)) {
        this.stackChrome(sv.selection, false);
        if (sv.container.parent === this.world) this.world.removeChild(sv.container);
        sv.destroy();
        this.shapeViews.delete(id);
      }
    }

    this.syncStacking(doc.cards);

    this.visibleCards = visible;

    // Las aristas se recalculan a partir de los rects del modelo, no de las
    // CardView: una arista puede cruzar la pantalla con sus dos extremos
    // culleados, así que sus vistas pueden no existir.
    this.edgeLayer.sync({
      edges: doc.edges,
      cards: doc.cards,
      zoom,
      viewport: rect,
      selected: this.selectedEdgeIds,
    });

    // El cromo de selección y el trazo en curso, arriba de todas las tarjetas.
    // Re-anexar un hijo existente sólo lo mueve al tope, pero igual invalida la
    // estructura: se hace sólo si algo se les metió encima.
    const kids = this.world.children;
    if (kids[kids.length - 2] !== this.chromeLayer || kids[kids.length - 1] !== this.inkLayer) {
      this.world.addChild(this.chromeLayer, this.inkLayer);
    }

    // Las etiquetas y los inputs son DOM, no world: hay que reposicionarlos a
    // mano cuando la cámara o lo que los ancla se mueven.
    this.groupLabels.sync(labels, (wx, wy) => this.camera.worldToScreen(wx, wy), zoom);
    this.textLabels.sync(textItems, (wx, wy) => this.camera.worldToScreen(wx, wy), zoom);
    this.commentLayer.sync(commentItems, (wx, wy) => this.camera.worldToScreen(wx, wy), zoom);
    this.groupRename.syncRect();
    this.syncEdgeLabels(doc.edges, zoom);
    this.edgeLabelInput.syncRect();
  }

  /**
   * Ordena las tarjetas montadas en el mundo según `doc.cards`, que es el orden
   * de apilado (ver `ArrangeOp`). Las vistas se anexan al mundo cuando entran
   * en pantalla, así que sin esto el orden de dibujo dependía de cuándo se vio
   * cada una por primera vez, y no coincidía con el del click.
   *
   * Corre por frame: es una pasada que sólo compara, y mueve algo únicamente
   * cuando el orden cambió.
   */
  private syncStacking(cards: Card[]): void {
    const world = this.world;
    let at = world.getChildIndex(this.edgeLayer.container) + 1;
    for (const card of cards) {
      const c = (this.views.get(card.id) ?? this.shapeViews.get(card.id))?.container;
      if (!c || c.parent !== world) continue;
      if (world.children[at] !== c) world.setChildIndex(c, at);
      at++;
    }
  }

  /**
   * Click derecho sobre una tarjeta: el menú de apilado, como el menú
   * contextual de Figma. Si la tarjeta no estaba elegida, pasa a ser LA
   * elegida; si ya era parte de una selección, el menú actúa sobre toda.
   */
  private onContextMenu(e: MouseEvent): void {
    e.preventDefault();
    if (this.mode !== "none") return;
    const w = this.camera.screenToWorld(e.offsetX, e.offsetY);
    const hit = this.cardAt(w.x, w.y);
    if (!hit || hit.type === "comment") return;
    if (!this.selectedIds.has(hit.id)) this.select(hit.id);
    void openContextMenu(e.clientX, e.clientY, ARRANGE_MENU, "dark").then((id) => {
      if (id) this.arrangeSelected(id as ArrangeOp);
    });
  }

  /** Mete o saca un cromo de selección de la capa de arriba de todo. */
  private stackChrome(g: Graphics, on: boolean): void {
    if (on) {
      if (g.parentRenderLayer !== this.chromeLayer) this.chromeLayer.attach(g);
    } else if (g.parentRenderLayer) {
      this.chromeLayer.detach(g);
    }
  }

  /**
   * Reapila la selección (menú del click derecho). Si no hay
   * nada elegido, o nada se movió, no pasa nada: tampoco queda un paso vacío
   * en el historial.
   */
  arrangeSelected(op: ArrangeOp): void {
    if (this.selectedIds.size === 0) return;
    this.store.getState().arrange(this.selectedIds, op);
  }

  /**
   * Texto de las aristas visibles.
   *
   * Debajo de `LABEL_MIN_ZOOM` se ocultan todas: de lejos el diagrama se lee por
   * su forma, y un enjambre de textos ilegibles sólo ensucia. Las líneas y sus
   * puntas se siguen dibujando, que son baratas y son la estructura.
   */
  private syncEdgeLabels(edges: Edge[], zoom: number): void {
    if (zoom < LABEL_MIN_ZOOM) {
      this.edgeLabels.sync([], (wx, wy) => this.camera.worldToScreen(wx, wy));
      return;
    }
    const items: EdgeLabelItem[] = [];
    for (const e of edges) {
      if (!e.label) continue;
      const mid = this.edgeLayer.midpointOf(e.id);
      if (!mid) continue; // culleada: no hay ruta de la que colgar la etiqueta
      items.push({
        id: e.id,
        x: mid.x,
        y: mid.y,
        text: e.label,
        color: inkColor(e.color ?? "#343331"),
        selected: this.selectedEdgeIds.has(e.id),
        hidden: this.edgeLabelInput.editingId === e.id,
      });
    }
    this.edgeLabels.sync(items, (wx, wy) => this.camera.worldToScreen(wx, wy));
  }

  /** La barrita de puntas y ruteo acompaña a la arista seleccionada. */
  private syncEdgeActions(): void {
    const id = this.selectedEdgeIds.size === 1 ? [...this.selectedEdgeIds][0] : null;
    const edge = id ? this.store.getState().doc.edges.find((e) => e.id === id) : undefined;
    const mid = id ? this.edgeLayer.midpointOf(id) : null;
    // Durante un gesto estorbaría, y mientras se edita la etiqueta el foco es
    // del input.
    if (!edge || !mid || this.mode !== "none" || this.edgeLabelInput.editingId) {
      this.edgeActions.hide();
      return;
    }
    const p = this.camera.worldToScreen(mid.x, mid.y);
    this.edgeActions.show({
      x: p.x,
      y: p.y,
      fromEnd: edge.fromEnd ?? DEFAULT_FROM_END,
      toEnd: edge.toEnd ?? DEFAULT_TO_END,
      route: edge.route ?? DEFAULT_ROUTE,
    });
  }

  /** Repaint the stroke currently being drawn (or clear it once committed). */
  private renderLiveStroke(): void {
    this.inkLayer.clear();
    if (this.currentStroke) {
      paintStroke(this.inkLayer, this.currentStroke, inkColor(this.toolbar.colorFor("draw")), INK_WIDTH);
    }
  }

  /** Lazily fill a `file` node's body from the vault, once per node. */
  private ensureFileLoaded(card: Card): void {
    if (card.type !== "file" || !card.file || card.markdown) return;
    // Las imágenes y los PDF se dibujan desde sus bytes, no desde markdown.
    if (isImagePath(card.file) || isPdfPath(card.file)) return;
    if (this.fileRequested.has(card.id) || !this.options.loadFile) return;
    this.fileRequested.add(card.id);
    this.options
      .loadFile(card.file)
      .then(
        (text) => this.queueLoadedFile(card.id, text || "*(empty document)*"),
        () => this.queueLoadedFile(card.id, `*No se pudo cargar* \`${card.file}\``),
      );
  }

  /**
   * Las lecturas terminan cada una por su lado, y con un `updateCard` por nota
   * abrir un lienzo de N notas copiaba el array de tarjetas N veces (O(N²)) y
   * notificaba al store otras tantas. Se juntan las que llegan dentro del mismo
   * frame y entran en una sola actualización.
   */
  private queueLoadedFile(id: string, markdown: string): void {
    if (this.disposed) return;
    this.loadedFiles.set(id, markdown);
    this.loadedFilesFrame ??= requestAnimationFrame(this.flushLoadedFiles);
  }

  private flushLoadedFiles = (): void => {
    this.loadedFilesFrame = null;
    const updatedAt = Date.now();
    const patches = new Map<string, Partial<Card>>();
    for (const [id, markdown] of this.loadedFiles) patches.set(id, { markdown, updatedAt });
    this.loadedFiles.clear();
    this.store.getState().updateCards(patches);
    for (const id of patches.keys()) this.cache.invalidate(id);
  };

  // ---- teardown -----------------------------------------------------------

  /** Stop rendering, drop all listeners and DOM, and free the Pixi context. */
  dispose(): void {
    this.disposed = true;
    if (this.loadedFilesFrame !== null) cancelAnimationFrame(this.loadedFilesFrame);
    for (const type of WAKE_EVENTS) {
      window.removeEventListener(type, this.wake, { capture: true });
    }
    document.fonts?.removeEventListener("loadingdone", this.wake);
    this.unsubscribeStore?.();
    this.cache.onReady = null;
    this.closeZoomMenu();
    this.commitCamera();
    this.textEditor.close();
    this.commentLayer.closeEditor();
    this.groupRename.cancel();
    this.resizeObserver?.disconnect();
    if (this.keepVisibleTimer !== null) clearTimeout(this.keepVisibleTimer);
    window.removeEventListener("pointerup", this.onWinPointerUp);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("copy", this.clipboard.onCopy);
    window.removeEventListener("paste", this.clipboard.onPaste);
    this.app.ticker.remove(this.frame);
    // El renderer sobrevive dos cuadros más (ver `releaseGpu`): sin parar el
    // ticker seguiría dibujando en un canvas que ya no está en el DOM.
    this.app.ticker.stop();
    this.hud?.destroy();
    this.edgeLayer.destroy();
    this.connect.destroy();
    // Detach the WebGPU view before destroying its renderer. Destroying an
    // attached context can briefly expose its cleared (black) buffer while a
    // markdown view is being mounted in the same host.
    this.canvas.style.visibility = "hidden";
    this.root.remove();
    this.releaseGpu();
    this.groupLabels.destroy();
    this.textLabels.destroy();
    this.commentLayer.destroy();
    this.edgeLabels.destroy();
    this.edgeActions.destroy();
    this.edgeLabelInput.cancel();
  }

  /**
   * Suelta el contexto de WebGPU, pero recién DESPUÉS del próximo cuadro pintado.
   *
   * Todo lo demás de `dispose()` es inmediato: para cuando esto se agenda, el
   * lienzo ya no está en el DOM y quien lo reemplaza ya está montado. Lo único
   * que queda es devolverle la memoria a la GPU, y eso no tiene por qué pasar
   * en el mismo cuadro en el que el navegador está tratando de mostrar la vista
   * nueva. Cuando pasaba ahí, ese cuadro salía negro: el parpadeo que se veía
   * "justo antes de que aparezca el texto" al salir de un canvas hacia una nota
   * era este destroy, metido en el cuadro que tenía que pintar la hoja.
   *
   * Son DOS `requestAnimationFrame` y no uno. El primero corre ANTES de que se
   * pinte el cuadro que lo agendó —destruir ahí es destruir en el cuadro que se
   * quería proteger—; el segundo corre con ese cuadro ya pintado y en pantalla.
   *
   * El costo es que el contexto vive unos milisegundos de más. Son dos cuadros
   * y un canvas a la vez, muy lejos del límite de contextos del navegador.
   */
  private releaseGpu(): void {
    const app = this.app;
    // Con WebGPU, Pixi suelta su referencia al dispositivo pero no lo destruye:
    // cada lienzo abierto dejaría uno vivo hasta que pase el GC. Se toma antes
    // del destroy, que es el que la borra.
    const device = (app.renderer as WebGPURenderer | undefined)?.gpu?.device;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        app.destroy(false, { children: true });
        device?.destroy();
      });
    });
  }
}
